import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalStyles from '@mui/material/GlobalStyles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import '@formio/js/dist/formio.full.min.css';
import React from 'react';
import { i18n } from 'i18n';
import type {
  CustomReportBindingGroupDto,
  CustomReportFormComponentDto,
  CustomReportFormSchemaDto
} from 'providers/api';
import { getFormioI18nOptions } from './customReportFormioI18n';
import { collectCustomReportFormComponents, normaliseCustomReportFormSchema } from './customReportFormSchema';

interface CustomReportFormioBuilderProps {
  schema?: CustomReportFormSchemaDto;
  onChange: (schema: CustomReportFormSchemaDto) => void;
  defaultLogoDataUri?: string;
  onLogoUploaded?: (logoDataUri: string) => Promise<void> | void;
  // Data sources a field can be bound to (auto-filled when the report is generated). When omitted, the
  // "Bound data" picker is hidden — the builder still works for purely static forms.
  bindingGroups?: CustomReportBindingGroupDto[];
}

interface CustomReportFormioRendererProps {
  schema?: CustomReportFormSchemaDto;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  defaultLogoDataUri?: string;
  onReadyChange?: (isReady: boolean) => void;
  printMode?: boolean;
}

type FormioInstance = {
  destroy?: (remove?: boolean) => void;
  form?: CustomReportFormSchemaDto;
  schema?: CustomReportFormSchemaDto;
  setForm?: (schema: CustomReportFormSchemaDto) => Promise<unknown> | unknown;
  submission?: {
    data?: Record<string, unknown>;
  };
  on?: (eventName: string, handler: (event?: { data?: Record<string, unknown> }) => void) => void;
  ready?: Promise<unknown>;
};

type FormioStatic = {
  builder: (
    element: HTMLElement,
    schema: CustomReportFormSchemaDto,
    options: Record<string, unknown>,
  ) => Promise<FormioInstance> | FormioInstance;
  createForm: (
    element: HTMLElement,
    schema: CustomReportFormSchemaDto,
    options: Record<string, unknown>,
  ) => Promise<FormioInstance> | FormioInstance;
};

const isFormioStatic = (value: unknown): value is FormioStatic => (
  Boolean(value)
  && typeof (value as FormioStatic).builder === 'function'
  && typeof (value as FormioStatic).createForm === 'function'
);

const formioScriptUrl = `${import.meta.env.BASE_URL}custom-report-vendor/formio.full.min.js`;
let formioScriptPromise: Promise<FormioStatic> | undefined;

const loadFormio = async () => {
  const globalFormio = (window as Window & { Formio?: FormioStatic }).Formio;
  if (isFormioStatic(globalFormio)) return globalFormio;

  if (formioScriptPromise) return formioScriptPromise;

  formioScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-custom-report-formio="true"]');
    const script = existingScript ?? document.createElement('script');

    script.dataset.customReportFormio = 'true';
    script.async = true;
    script.src = formioScriptUrl;

    script.addEventListener('load', () => {
      const loadedFormio = (window as Window & { Formio?: FormioStatic }).Formio;
      if (isFormioStatic(loadedFormio)) {
        resolve(loadedFormio);
        return;
      }

      reject(new Error('Unable to load the Form.io browser bundle.'));
    }, { once: true });

    script.addEventListener('error', () => {
      reject(new Error('Unable to load the Form.io browser bundle.'));
    }, { once: true });

    if (!existingScript) {
      document.head.appendChild(script);
    }
  });

  try {
    return await formioScriptPromise;
  } catch (error) {
    formioScriptPromise = undefined;
    throw error;
  }
};

const formioBuilderOptions = {
  noDefaultSubmitButton: true,
  // Don't pop the settings dialog every time a field is dropped — the user edits a field by
  // double-clicking it instead (wired up in the builder effect).
  noNewEdit: true,
  builder: {
    // Hide Form.io's default palette groups and expose a single curated, friendly set instead —
    // including Signature and Date, which live in the (disabled) "advanced" group by default.
    basic: false,
    advanced: false,
    data: false,
    premium: false,
    resource: false,
    fields: {
      title: i18n._('Fields'),
      default: true,
      weight: 0,
      components: {
        textfield: true,
        textarea: true,
        number: true,
        datetime: true,
        checkbox: true,
        selectboxes: true,
        radio: true,
        select: true,
        signature: true,
        // Free-text paragraph you type into and colour word-by-word (double-click to edit).
        content: { title: i18n._('Text block'), weight: 20 },
      },
    },
    layout: {
      title: i18n._('Layout'),
      weight: 10,
      components: {
        panel: true,
        fieldset: true,
        columns: true,
        table: true,
        tabs: true,
        well: false,
        htmlelement: false,
      },
    },
  },
};

const nonResizableComponentTypes = new Set([
  'button',
  'columns',
  'content',
  'container',
  'fieldset',
  'hidden',
  'htmlelement',
  'panel',
  'submit',
  'table',
  'tabs',
  'well',
]);

const sizeValueFromComponent = (component: CustomReportFormComponentDto, key: 'customReportWidth' | 'customReportHeight') => {
  const value = component[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const isResizableComponent = (component: CustomReportFormComponentDto) => (
  Boolean(component.key && component.input && !nonResizableComponentTypes.has(component.type))
);

const collectResizableComponents = (components: CustomReportFormComponentDto[] | undefined): CustomReportFormComponentDto[] => (
  (components ?? []).flatMap((component) => [
    ...(isResizableComponent(component) ? [component] : []),
    ...collectResizableComponents(component.components),
    ...(Array.isArray(component.columns) ? component.columns.flatMap((column) => collectResizableComponents(column.components)) : []),
    ...(Array.isArray(component.rows)
      ? component.rows.flatMap((row) => (
        Array.isArray(row) ? row.flatMap((cell) => collectResizableComponents(cell.components)) : []
      ))
      : []),
  ])
);

const formioComponentElementFor = (container: HTMLElement, key: string) => (
  Array.from(container.querySelectorAll<HTMLElement>('.formio-component'))
    .find((element) => element.classList.contains(`formio-component-${key}`))
);

const applyStoredComponentSizes = (container: HTMLElement, schema: CustomReportFormSchemaDto) => {
  collectResizableComponents(schema.components).forEach((component) => {
    if (!component.key) return;

    const element = formioComponentElementFor(container, component.key);
    if (!element) return;

    element.dataset.customReportResizable = 'true';
    element.dataset.customReportKey = component.key;

    const width = sizeValueFromComponent(component, 'customReportWidth');
    const height = sizeValueFromComponent(component, 'customReportHeight');

    if (width) {
      element.style.width = `${width}px`;
      element.style.maxWidth = '100%';
    }

    if (height) {
      element.style.minHeight = `${height}px`;
    }
  });
};

const updateComponents = (
  components: CustomReportFormComponentDto[] | undefined,
  update: (component: CustomReportFormComponentDto) => CustomReportFormComponentDto,
): CustomReportFormComponentDto[] => (components ?? []).map((component) => {
  const next = update(component);

  return {
    ...next,
    ...(Array.isArray(next.components) ? { components: updateComponents(next.components, update) } : {}),
    ...(Array.isArray(next.columns)
      ? { columns: next.columns.map((column) => ({ ...column, components: updateComponents(column.components, update) })) }
      : {}),
    ...(Array.isArray(next.rows)
      ? {
        rows: next.rows.map((row) => (Array.isArray(row)
          ? row.map((cell) => ({ ...cell, components: updateComponents(cell.components, update) }))
          : row)),
      }
      : {}),
  };
});

const withComponentSize = (
  schema: CustomReportFormSchemaDto,
  key: string,
  width: number,
  height: number,
): CustomReportFormSchemaDto => ({
  ...schema,
  components: updateComponents(schema.components, (component) => (
    component.key === key && isResizableComponent(component)
      ? { ...component, customReportWidth: width, customReportHeight: height }
      : component
  )),
});

// Our per-field style props live under these keys on the component. Form.io may drop unknown props when
// it rebuilds its own schema, so after every 'change' we re-merge them from our source-of-truth schema.
const CUSTOM_STYLE_KEYS = ['customReportWidth', 'customReportHeight', 'customReportLabelColor', 'customReportRichText', 'customReportBinding'] as const;

const mergeStoredComponentStyles = (
  schema: CustomReportFormSchemaDto,
  sourceSchema: CustomReportFormSchemaDto,
): CustomReportFormSchemaDto => {
  const stored = collectCustomReportFormComponents(sourceSchema.components).reduce<Map<string, Record<string, unknown>>>(
    (next, component) => {
      if (!component.key) return next;
      const picked: Record<string, unknown> = {};
      CUSTOM_STYLE_KEYS.forEach((styleKey) => {
        if (component[styleKey] !== undefined) picked[styleKey] = component[styleKey];
      });
      if (Object.keys(picked).length) next.set(component.key, picked);
      return next;
    },
    new Map(),
  );

  return {
    ...schema,
    components: updateComponents(schema.components, (component) => {
      const styles = component.key ? stored.get(component.key) : undefined;
      return styles ? { ...component, ...styles } : component;
    }),
  };
};

// Choice fields whose options are edited inline (no settings dialog).
const CHOICE_TYPES = new Set(['select', 'radio', 'selectboxes']);
// Layout components still use the settings dialog (double-click opens it) — there's no single label to
// rename inline; they need their own config (columns, table, tabs…).
const LAYOUT_TYPES = new Set(['columns', 'table', 'panel', 'fieldset', 'tabs', 'content', 'well', 'container']);

const componentOptions = (component: CustomReportFormComponentDto): Array<{ label: string; value: string }> => {
  const raw = component.type === 'select' ? component.data?.values : component.values;
  return (Array.isArray(raw) ? raw : []).map((option) => ({ label: option.label ?? '', value: option.value ?? '' }));
};

const optionValue = (label: string, index: number) => (
  label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `option_${index + 1}`
);

const withComponentOptions = (
  schema: CustomReportFormSchemaDto,
  key: string,
  options: Array<{ label: string; value: string }>,
): CustomReportFormSchemaDto => ({
  ...schema,
  components: updateComponents(schema.components, (component) => {
    if (component.key !== key) return component;
    const values = options.map((option, index) => ({ label: option.label, value: option.value || optionValue(option.label, index) }));
    if (component.type === 'select') return { ...component, data: { ...(component.data ?? {}), values } };
    return { ...component, values };
  }),
});

// Builder-time LABEL colour (a coloured field heading). Distinct from the fill-time value colouring —
// this only tints the label, so it never "colours everything".
const LABEL_COLOURS = ['#111827', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0284c7', '#4529de', '#7c3aed'];

const withComponentLabelColour = (
  schema: CustomReportFormSchemaDto,
  key: string,
  colour?: string,
): CustomReportFormSchemaDto => ({
  ...schema,
  components: updateComponents(schema.components, (component) => {
    if (component.key !== key) return component;
    const next = { ...component };
    if (colour) next.customReportLabelColor = colour;
    else delete (next as Record<string, unknown>).customReportLabelColor;
    return next;
  }),
});

const withComponentRequired = (
  schema: CustomReportFormSchemaDto,
  key: string,
  required: boolean,
): CustomReportFormSchemaDto => ({
  ...schema,
  components: updateComponents(schema.components, (component) => (
    component.key === key
      ? { ...component, validate: { ...(component.validate ?? {}), required } }
      : component
  )),
});

// Friendly names for the settings panel's type chip.
const FIELD_TYPE_LABELS: Record<string, string> = {
  textfield: 'Text Field',
  textarea: 'Text Area',
  number: 'Number',
  datetime: 'Date / Time',
  checkbox: 'Checkbox',
  selectboxes: 'Select Boxes',
  select: 'Select',
  radio: 'Radio',
  signature: 'Signature',
  content: 'Text block',
  datagrid: 'Table (repeating)',
  columns: 'Columns',
  table: 'Table',
  panel: 'Panel',
  fieldset: 'Field Set',
  tabs: 'Tabs',
  well: 'Well',
};

// Bind a field to a BFP data source (or clear it). The binding key is stored under customReportBinding
// and auto-fills the field when the report is generated. Kept as a plain custom prop so it round-trips
// through save/load with no schema change.
const withComponentBinding = (
  schema: CustomReportFormSchemaDto,
  key: string,
  bindingKey?: string,
): CustomReportFormSchemaDto => ({
  ...schema,
  components: updateComponents(schema.components, (component) => {
    if (component.key !== key) return component;
    const next = { ...component };
    if (bindingKey) next.customReportBinding = bindingKey;
    else delete (next as Record<string, unknown>).customReportBinding;
    return next;
  }),
});

const bindingKeyForComponent = (component: CustomReportFormComponentDto): string | undefined => (
  typeof component.customReportBinding === 'string' ? component.customReportBinding : undefined
);

// Tags every bound field on the canvas with data-custom-report-bound="<label>" so a CSS chip marks it
// as dynamic. Runs only when called (post-build / post-change), never during a drag — same rule as the
// other DOM helpers here.
const applyBindingBadges = (
  container: HTMLElement,
  schema: CustomReportFormSchemaDto,
  labelForKey: (bindingKey: string) => string,
) => {
  container.querySelectorAll('[data-custom-report-bound]').forEach((element) => element.removeAttribute('data-custom-report-bound'));
  collectCustomReportFormComponents(schema.components).forEach((component) => {
    const bindingKey = component.key ? bindingKeyForComponent(component) : undefined;
    if (!component.key || !bindingKey) return;
    formioComponentElementFor(container, component.key)?.setAttribute('data-custom-report-bound', labelForKey(bindingKey));
  });
};

const collectLabelColours = (schema: CustomReportFormSchemaDto): Record<string, string> => (
  collectCustomReportFormComponents(schema.components).reduce<Record<string, string>>((acc, component) => {
    if (component.key && typeof component.customReportLabelColor === 'string') {
      return { ...acc, [component.key]: component.customReportLabelColor };
    }
    return acc;
  }, {})
);

// Text Field / Text Area whose value is authored as rich, coloured text on the build canvas.
const RICH_TEXT_TYPES = new Set(['textfield', 'textarea']);

const escapeHtmlText = (value: string) => value
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/\r\n|\r|\n/g, '<br>');

const withComponentRichText = (
  schema: CustomReportFormSchemaDto,
  key: string,
  html: string,
): CustomReportFormSchemaDto => ({
  ...schema,
  components: updateComponents(schema.components, (component) => (
    component.key === key ? { ...component, customReportRichText: html } : component
  )),
});

// Replace the plain input of each rich-text field with a coloured display of its authored HTML. Runs
// only when called (post-drop / initial), never during a drag, so it can't disturb drag-and-drop.
const applyStoredRichText = (container: HTMLElement, schema: CustomReportFormSchemaDto) => {
  collectCustomReportFormComponents(schema.components).forEach((component) => {
    if (!component.key || !RICH_TEXT_TYPES.has(component.type)) return;
    const html = typeof component.customReportRichText === 'string' ? component.customReportRichText : undefined;
    const element = formioComponentElementFor(container, component.key);
    const input = element?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input.form-control, textarea');
    if (!element || !input) return;

    let display = element.querySelector<HTMLElement>('.crt-display');
    if (!html) {
      if (display) { display.remove(); input.style.display = ''; }
      return;
    }
    if (!display) {
      display = document.createElement('div');
      display.className = 'form-control crt-display';
      input.style.display = 'none';
      input.insertAdjacentElement('afterend', display);
    }
    display.dataset.customReportKey = component.key;
    if (display.dataset.customReportEditing !== 'true') display.innerHTML = html;
  });
};

// sx rules that colour only the LABEL (heading) of each field within `scope`, not its value/input.
const labelColourSx = (scope: string, colours: Record<string, string>) => (
  Object.entries(colours).reduce<Record<string, { color: string }>>((acc, [key, colour]) => {
    const selector = [
      `& ${scope} .formio-component-${key} > label`,
      `& ${scope} .formio-component-${key} > .col-form-label`,
      `& ${scope} .formio-component-${key} > .control-label`,
      `& ${scope} .formio-component-${key} > legend`,
    ].join(', ');
    return { ...acc, [selector]: { color: colour } };
  }, {})
);

// True while Form.io/dragula is mid-drag (the mirror clone exists on <body>). We never touch the
// builder DOM during a drag — that was what broke drag-and-drop the first time round.
const isBuilderDragging = () => Boolean(document.querySelector('.gu-mirror'));

// Per-field resize WITHOUT a live MutationObserver: sizes are (re)applied and new fields are observed
// only when the caller calls sync() — which we drive off Form.io's post-drop 'change' event and an
// initial pass. A ResizeObserver captures manual resizes. Both paths bail out while a drag is active.
const attachFieldResize = (
  container: HTMLElement,
  getSchema: () => CustomReportFormSchemaDto,
  setSchema: (schema: CustomReportFormSchemaDto) => void,
) => {
  let pointerDownKey: string | undefined;
  let pointerDownAt = 0;
  const controller = new AbortController();

  const resizeObserver = new ResizeObserver((entries) => {
    if (isBuilderDragging()) return;
    entries.forEach((entry) => {
      const element = entry.target as HTMLElement;
      const key = element.dataset.customReportKey;
      if (!key || pointerDownKey !== key || Date.now() - pointerDownAt > 3000) return;

      const width = Math.round(element.offsetWidth);
      const height = Math.round(element.offsetHeight);
      const schema = getSchema();
      const current = collectResizableComponents(schema.components).find((component) => component.key === key);

      if (
        current
        && Math.abs((sizeValueFromComponent(current, 'customReportWidth') ?? 0) - width) < 2
        && Math.abs((sizeValueFromComponent(current, 'customReportHeight') ?? 0) - height) < 2
      ) {
        return;
      }

      setSchema(withComponentSize(schema, key, width, height));
    });
  });

  const sync = () => {
    if (isBuilderDragging()) return;
    const schema = getSchema();
    applyStoredComponentSizes(container, schema);
    collectResizableComponents(schema.components).forEach((component) => {
      if (!component.key) return;

      const componentKey = component.key;
      const element = formioComponentElementFor(container, componentKey);
      if (!element || element.dataset.customReportResizeObserved === 'true') return;

      element.dataset.customReportResizeObserved = 'true';

      // Grabbing the native resize handle (bottom-right corner) must resize the field, not start a
      // Form.io field drag. When the pointer goes down in the corner zone, keep the builder's
      // drag-and-drop from seeing it (stopPropagation — NOT preventDefault, so the browser's own resize
      // still runs), and block any HTML5 drag it would start until the pointer is released.
      let resizingFromHandle = false;
      const isOnResizeHandle = (event: PointerEvent | MouseEvent) => {
        const rect = element.getBoundingClientRect();
        const zone = 18;
        return event.clientX >= rect.right - zone && event.clientY >= rect.bottom - zone;
      };
      element.addEventListener('pointerdown', (event) => {
        if (!isOnResizeHandle(event)) return;
        resizingFromHandle = true;
        pointerDownKey = componentKey;
        pointerDownAt = Date.now();
        event.stopPropagation();
      }, { signal: controller.signal, capture: true });
      element.addEventListener('mousedown', (event) => {
        if (isOnResizeHandle(event)) event.stopPropagation();
      }, { signal: controller.signal, capture: true });
      element.addEventListener('dragstart', (event) => {
        if (!resizingFromHandle) return;
        event.preventDefault();
        event.stopPropagation();
      }, { signal: controller.signal, capture: true });
      window.addEventListener('pointerup', () => { resizingFromHandle = false; }, { signal: controller.signal });

      resizeObserver.observe(element);
    });
  };

  return {
    sync,
    detach: () => {
      controller.abort();
      resizeObserver.disconnect();
    },
  };
};

// The component edit dialog normally splits settings across Display / Data / Validation tabs (plus the
// advanced tabs we hide). That tab-hopping is confusing for a report author, so we flatten it: show the
// Display, Data and Validation panes stacked on one scrolling page and hide the tab strip. Panes are
// matched to tabs by the nav-link label (not by position) so layout components — which have a different
// tab set — never accidentally expose their advanced panes.
const FLATTENED_EDIT_TABS = new Set(['display', 'data', 'validation']);

const styleComponentEditDialog = (dialog: HTMLElement) => {
  // 1) Turn the dialog into a right-side drawer. Inline styles (set after Form.io renders) beat both our
  //    stylesheet and Form.io's own inline modal styles, which was why the CSS-only version stayed wide.
  const content = dialog.querySelector<HTMLElement>('.formio-dialog-content');
  if (content && content.dataset.customReportDrawer !== 'true') {
    content.dataset.customReportDrawer = 'true';
    content.style.setProperty('margin', '0 0 0 auto', 'important');
    content.style.setProperty('max-width', '440px', 'important');
    content.style.setProperty('width', 'min(440px, 100vw)', 'important');
    content.style.setProperty('height', '100vh', 'important');
    content.style.setProperty('max-height', '100vh', 'important');
    content.style.setProperty('border-radius', '16px 0 0 16px', 'important');
  }

  // 2) Flatten the tab strip into one scrolling page: show Display / Data / Validation stacked (Data holds
  //    the options grid for choice fields), hide the tab nav and the advanced panes. Panes are matched to
  //    tabs by nav-link label, and we query `.tab-pane` directly (Form.io doesn't wrap them in .tab-content).
  const nav = dialog.querySelector<HTMLElement>('ul.nav-tabs, ul.nav');
  const panes = Array.from(dialog.querySelectorAll<HTMLElement>('.tab-pane'));
  if (nav && panes.length) {
    const labels = Array.from(nav.querySelectorAll<HTMLElement>('.nav-link, a'))
      .map((link) => (link.textContent ?? '').trim().toLowerCase());
    panes.forEach((pane, index) => {
      const paneEl = pane;
      const show = FLATTENED_EDIT_TABS.has(labels[index] ?? '');
      paneEl.style.setProperty('display', show ? 'block' : 'none', 'important');
      paneEl.dataset.customReportEditPane = show ? (labels[index] ?? '') : 'hidden';
    });
    nav.style.setProperty('display', 'none', 'important');
  }
};

// Watches for the (body-appended) component edit dialog and restyles it whenever Form.io (re)renders it.
// We observe childList only, so our own inline-style writes never re-trigger the observer.
const attachComponentEditDialogFlattening = () => {
  const process = () => {
    document.querySelectorAll<HTMLElement>('.formio-dialog').forEach((dialog) => {
      if (dialog.querySelector('.formio-dialog-content') && dialog.querySelector('ul.nav-tabs, ul.nav, .component-edit-container')) {
        styleComponentEditDialog(dialog);
      }
    });
  };

  const observer = new MutationObserver(process);
  observer.observe(document.body, { childList: true, subtree: true });
  process();

  return () => observer.disconnect();
};

const readLogoFileAsDataUri = (file?: File) => new Promise<string>((resolve, reject) => {
  if (!file) {
    reject(new Error('No logo file selected.'));
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === 'string') {
      resolve(reader.result);
      return;
    }

    reject(new Error('Unable to read logo file.'));
  };
  reader.onerror = () => reject(reader.error ?? new Error('Unable to read logo file.'));
  reader.readAsDataURL(file);
});

const baseEditorShellSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: 1,
};

const formioButtonSelector = [
  '& .formio-builder button',
  '& .formio-builder .btn',
  '& .formio-dialog button',
  '& .formio-dialog .btn',
].join(', ');

const formioButtonHoverSelector = [
  '& .formio-builder button:hover',
  '& .formio-builder .btn:hover',
  '& .formio-dialog button:hover',
  '& .formio-dialog .btn:hover',
].join(', ');

const formioPrimaryButtonSelector = [
  '& .formio-builder .btn-primary',
  '& .formio-builder button.btn-primary',
  '& .formio-dialog .btn-primary',
  '& .formio-dialog button.btn-primary',
].join(', ');

const formioDangerButtonSelector = [
  '& .formio-builder .btn-danger',
  '& .formio-builder button.btn-danger',
  '& .formio-dialog .btn-danger',
  '& .formio-dialog button.btn-danger',
].join(', ');

const formioInputSelector = [
  '& .formio-builder input',
  '& .formio-builder textarea',
  '& .formio-builder select',
  '& .formio-dialog input',
  '& .formio-dialog textarea',
  '& .formio-dialog select',
].join(', ');

const hiddenFormioDisplaySettingsSelector = [
  '.formio-dialog .formio-component-inputMask',
  '.formio-dialog .formio-component-displayMask',
  '.formio-dialog .formio-component-applyMaskOn',
  '.formio-dialog .formio-component-mask',
  '.formio-dialog .formio-component-hideInput',
  '.formio-dialog .formio-component-hideInputLabels',
  '.formio-dialog .formio-component-allowMultipleMasks',
  '.formio-dialog .formio-component-inputMasks',
  '.formio-dialog .formio-component-inputMaskPlaceholderChar',
  '.formio-dialog .formio-component-customClass',
  '.formio-dialog .formio-component-tabindex',
  '.formio-dialog .formio-component-tabIndex',
  '.formio-dialog .formio-component-autocomplete',
  '.formio-dialog .formio-component-hidden',
  '.formio-dialog .formio-component-hideLabel',
  '.formio-dialog .formio-component-disabled',
  '.formio-dialog .formio-component-modalEdit',
  '.formio-dialog .formio-component-tableView',
  '.formio-dialog .formio-component-autofocus',
  '.formio-dialog .formio-component-spellcheck',
  '.formio-dialog .formio-component-showWordCount',
  '.formio-dialog .formio-component-showCharCount',
  '.formio-dialog .formio-component-truncateMultipleSpaces',
  // Further declutter: layout/label positioning and data-management settings a report author never needs.
  '.formio-dialog .formio-component-labelPosition',
  '.formio-dialog .formio-component-optionsLabelPosition',
  '.formio-dialog .formio-component-labelWidth',
  '.formio-dialog .formio-component-labelMargin',
  '.formio-dialog .formio-component-widget',
  '.formio-dialog .formio-component-persistent',
  '.formio-dialog .formio-component-protected',
  '.formio-dialog .formio-component-dbIndex',
  '.formio-dialog .formio-component-encrypted',
  '.formio-dialog .formio-component-redrawOn',
  '.formio-dialog .formio-component-clearOnHide',
  '.formio-dialog .formio-component-calculateServer',
  '.formio-dialog .formio-component-dataType',
  '.formio-dialog .formio-component-refreshOnChange',
  '.formio-dialog .formio-component-refreshOn',
  // Display tab: keep Label / Placeholder / Description only.
  '.formio-dialog .formio-component-tooltip',
  '.formio-dialog .formio-component-prefix',
  '.formio-dialog .formio-component-suffix',
  // Data tab: keep Default Value only.
  '.formio-dialog .formio-component-multiple',
  '.formio-dialog .formio-component-inputFormat',
  '.formio-dialog .formio-component-case',
  '.formio-dialog .formio-component-customDefaultValuePanel',
  '.formio-dialog .formio-component-calculateValuePanel',
  '.formio-dialog .formio-component-allowCalculateOverride',
  // Validation tab: keep Required only.
  '.formio-dialog .formio-component-validateOn',
  '.formio-dialog .formio-component-unique',
  '.formio-dialog .formio-component-validateWhenHidden',
  '.formio-dialog .formio-component-minLength',
  '.formio-dialog .formio-component-maxLength',
  '.formio-dialog .formio-component-minWords',
  '.formio-dialog .formio-component-maxWords',
  '.formio-dialog .formio-component-pattern',
  '.formio-dialog .formio-component-errorLabel',
  '.formio-dialog .formio-component-customMessage',
  '.formio-dialog .formio-component-customValidationPanel',
  '.formio-dialog .formio-component-jsonValidationPanel',
].join(', ');

// Component edit tabs are: 1 Display, 2 Data, 3 Validation, 4 API, 5 Conditional, 6 Logic, 7 Layout.
// We hide the advanced tabs (API/Conditional/Logic/Layout) but must KEEP the Data tab — it holds the
// Values grid for choice components (Select Boxes / Select / Radio); hiding it makes them unfillable.
const hiddenFormioEditTabsSelector = [
  '.formio-dialog ul.nav > li:nth-child(4)',
  '.formio-dialog ul.nav > li:nth-child(5)',
  '.formio-dialog ul.nav > li:nth-child(6)',
  '.formio-dialog ul.nav > li:nth-child(7)',
  '.formio-dialog .nav-tabs > li:nth-child(4)',
  '.formio-dialog .nav-tabs > li:nth-child(5)',
  '.formio-dialog .nav-tabs > li:nth-child(6)',
  '.formio-dialog .nav-tabs > li:nth-child(7)',
  '.formio-dialog .nav-tabs > .nav-item:nth-child(4)',
  '.formio-dialog .nav-tabs > .nav-item:nth-child(5)',
  '.formio-dialog .nav-tabs > .nav-item:nth-child(6)',
  '.formio-dialog .nav-tabs > .nav-item:nth-child(7)',
  '.formio-dialog .nav-tabs > .nav-link:nth-child(4)',
  '.formio-dialog .nav-tabs > .nav-link:nth-child(5)',
  '.formio-dialog .nav-tabs > .nav-link:nth-child(6)',
  '.formio-dialog .nav-tabs > .nav-link:nth-child(7)',
].join(', ');

// Every text-ish control Form.io renders on the canvas. Checkboxes/radios are excluded — they're glyphs,
// not wells, and a background makes them look disabled.
const builderCanvasFieldSelector = [
  '& .formarea .formio-component input:not([type="checkbox"]):not([type="radio"])',
  '& .formarea .formio-component textarea',
  '& .formarea .formio-component select',
  '& .formarea .formio-component .form-control',
].join(', ');

const builderCanvasFieldFocusSelector = [
  '& .formarea .formio-component input:not([type="checkbox"]):not([type="radio"]):focus',
  '& .formarea .formio-component textarea:focus',
  '& .formarea .formio-component select:focus',
  '& .formarea .formio-component .form-control:focus',
].join(', ');

const builderResizableInputSelector = [
  '& .formarea .formio-component[data-custom-report-resizable="true"] input:not([type="checkbox"]):not([type="radio"])',
  '& .formarea .formio-component[data-custom-report-resizable="true"] textarea',
  '& .formarea .formio-component[data-custom-report-resizable="true"] select',
  '& .formarea .formio-component[data-custom-report-resizable="true"] .form-control',
  '& .formarea .formio-component[data-custom-report-resizable="true"] .choices',
].join(', ');

const rendererResizableInputSelector = [
  '& .formio-component[data-custom-report-resizable="true"] input:not([type="checkbox"]):not([type="radio"])',
  '& .formio-component[data-custom-report-resizable="true"] textarea',
  '& .formio-component[data-custom-report-resizable="true"] select',
  '& .formio-component[data-custom-report-resizable="true"] .form-control',
  '& .formio-component[data-custom-report-resizable="true"] .choices',
].join(', ');

// The bundled Form.io CSS ships the Bootstrap col-* class NAMES but not their grid widths (same gap that
// once collapsed the palette/canvas split), so a Columns component stacks its columns vertically and
// reads as "rows". Recreate the grid under `scope`: rows flex, columns sized by their col-* class — so
// Columns lays out side-by-side on the canvas, the fill view and the printed PDF alike.
const bootstrapGridSx = (scope: string) => ({
  [`& ${scope} .row`]: {
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: '-8px',
    marginRight: '-8px',
  },
  [`& ${scope} .row > [class*="col-"]`]: {
    boxSizing: 'border-box',
    flex: '1 1 0',
    minWidth: 0,
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  ...Object.fromEntries(Array.from({ length: 12 }, (_, index) => index + 1).map((size) => [
    ['xs', 'sm', 'md', 'lg'].map((breakpoint) => `& ${scope} .row > .col-${breakpoint}-${size}`).join(', '),
    { flex: `0 0 ${((size / 12) * 100).toFixed(4)}%`, maxWidth: `${((size / 12) * 100).toFixed(4)}%` },
  ])),
});

// Document neutrals for the builder canvas. Form.io/Bootstrap ship cool blue-greys (#fbfcfe paper,
// #d1d5db borders) that read as a foreign design system next to the app's indigo — and a pure-white slab
// glares against the dark chrome. These are the same neutrals biased toward the primary (#4529de): a light
// grey paper with slightly deeper grey field "wells", so fields stay legible without white-on-white.
// Screen only — the exported PDF (printRendererShellSx) keeps white paper.
// TRIAL — dark canvas. The canvas now speaks the app's dark form language (matching the Stope Recipe
// dialog: dark surface, grey-bordered wells, light ink) instead of light paper.
// Known trade-off: the exported PDF still prints white paper with dark ink, so the builder no longer shows
// what prints. Author-chosen dark label/text colours also read as invisible here but print correctly.
// To revert, restore the light set:
//   PAPER '#e8e6f1' · FIELD '#dcd9e9' · FIELD_BORDER '#bdb9cf' · EDGE '#b5b0c8' · INK '#111827' · LABEL '#111827'
const CANVAS_PAPER = '#333131';
const CANVAS_FIELD = '#2b2929';
const CANVAS_FIELD_BORDER = 'rgba(255,255,255,0.23)';
const CANVAS_EDGE = 'rgba(255,255,255,0.16)';
const CANVAS_INK = '#e9e8ee';
const CANVAS_LABEL = '#b6b4c0';

const builderShellSx = {
  ...baseEditorShellSx,
  // Dark chrome to match the app; the form canvas (.formarea) stays a light "paper" document.
  bgcolor: '#242222',
  borderColor: 'rgba(255,255,255,0.10)',
  color: '#cbd5e1',
  maxHeight: 'calc(100vh - 150px)',
  overflow: 'auto',
  '& .formarea .formio-component, & .formarea .formio-form': {
    color: CANVAS_INK,
    fontFamily: 'inherit',
  },
  // Datagrids (e.g. Pours, with many columns) otherwise render as a wide horizontal table that needs
  // sideways scrolling and doesn't match the generated document. In the builder canvas, stack each row's
  // fields vertically so the section reads top-to-bottom like the PDF. Static CSS only — no effect on
  // drag-and-drop.
  '& .formarea .formio-component-datagrid table': {
    display: 'block',
    width: '100%',
  },
  '& .formarea .formio-component-datagrid thead': {
    display: 'none',
  },
  '& .formarea .formio-component-datagrid tbody, & .formarea .formio-component-datagrid tbody tr': {
    display: 'block',
    width: '100%',
  },
  '& .formarea .formio-component-datagrid tbody td': {
    borderTop: '1px solid rgba(255,255,255,0.08)',
    display: 'block',
    padding: '8px 4px',
    width: '100%',
  },
  '& .formarea .formio-component-datagrid tbody td:first-of-type': {
    borderTop: 'none',
  },
  '& .formio-builder': {
    backgroundColor: '#242222',
  },
  '& .formio-builder .row': {
    marginLeft: 0,
    marginRight: 0,
  },
  // Side-by-side layout for the Columns component (see bootstrapGridSx). To build a "row of fields",
  // drop a Columns component — each one is a row; there is no separate Rows component in Form.io.
  ...bootstrapGridSx('.formarea'),
  // Fields dropped into Table cells are bare inputs in a grid — their auto label ("Number") is noise
  // there, so hide it. Column headings come from a "Text block" placed in the top row instead.
  '& .formarea .formio-component-table td .formio-component > label:not(.form-check-label), & .formarea .formio-component-table td .formio-component > .col-form-label': {
    display: 'none',
  },
  // Force the two-column WYSIWYG layout (palette on the left, canvas on the right). The bundled Form.io
  // CSS ships the Bootstrap col-* class names but not their grid widths, so without this the palette
  // collapses into full-width bars stacked on top of the canvas.
  '& .formbuilder': {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '16px',
    margin: 0,
  },
  '& .formarea, & .formarea .card, & .formarea .card-body, & .formarea .list-group-item': {
    backgroundColor: CANVAS_PAPER,
    color: CANVAS_INK,
  },
  '& .formcomponents': {
    alignSelf: 'stretch',
    backgroundColor: '#2f2d2d',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: '10px',
    color: '#cbd5e1',
    flex: '0 0 260px',
    maxHeight: 'calc(100vh - 200px)',
    maxWidth: 260,
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: '10px',
    position: 'sticky',
    top: 0,
    width: 260,
  },
  '& .formcomponents .card, & .formcomponents .card-header, & .formcomponents .card-body, & .formcomponents .list-group-item': {
    backgroundColor: 'transparent',
    borderColor: 'rgba(255,255,255,0.08)',
    color: '#cbd5e1',
  },
  // Form.io makes this inner wrapper its own sticky scroller (sticky, max-height 100vh) — with the
  // panel itself already scrolling, that nested stickiness made content overlap on short viewports.
  // Flatten it so the palette groups and the docked field settings flow in one column.
  '& .formcomponents .builder-sidebar_scroll': {
    maxHeight: 'none',
    overflow: 'visible',
    position: 'static',
  },
  // Keep every palette group (Fields AND Layout) expanded. Form.io renders each group as a Bootstrap
  // accordion: `#group-<key>.accordion-collapse` is the collapsible element, and only the group marked
  // `default: true` gets `show` — so Layout rendered as a header with nothing under it. Force them open.
  // The inner `#group-container-<key>` is left alone so its own `d-grid` button layout still applies.
  '& .formcomponents .accordion-collapse': {
    display: 'block !important',
    height: 'auto !important',
    visibility: 'visible !important',
  },
  // Constrain each field to a document width so its action buttons (edit/move/copy/remove) sit near the
  // field instead of at the far right edge of the very wide canvas. The drop zone stays full width.
  '& .formarea .builder-component': {
    maxWidth: 820,
  },
  '& .formarea': {
    backgroundColor: CANVAS_PAPER,
    border: `1.5px dashed ${CANVAS_EDGE}`,
    borderRadius: '12px',
    flex: '1 1 auto',
    minHeight: 'calc(100vh - 230px)',
    minWidth: 0,
    padding: '16px',
    // Reserved letterhead area: the logo band is absolutely overlaid on this top padding (see the
    // builder JSX), so the palette runs full height with no dead space above it.
    paddingTop: '130px',
    position: 'relative',
  },
  // Palette items are a vertical, full-width stacked list inside the left panel.
  '& .formcomponent': {
    alignItems: 'center',
    backgroundColor: '#3a3838',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    boxShadow: 'none',
    color: '#e5e7eb',
    cursor: 'grab',
    display: 'flex',
    fontSize: '12.5px',
    fontWeight: 600,
    gap: '7px',
    margin: '0 0 5px',
    minHeight: '32px',
    padding: '7px 9px',
    width: '100%',
    transition: 'background-color .1s ease, border-color .1s ease, box-shadow .1s ease, transform .1s ease',
  },
  '& .formcomponent:hover': {
    backgroundColor: 'rgba(105,103,173,0.22)',
    borderColor: '#6d5cff',
    boxShadow: 'none',
    transform: 'translateY(-1px)',
  },
  '& .formarea .formio-component[data-custom-report-resizable="true"]': {
    maxWidth: '100%',
    minHeight: 52,
    minWidth: 140,
    overflow: 'auto',
    paddingRight: '4px',
    resize: 'both',
  },
  // The field currently selected for colouring.
  '& .formarea [data-custom-report-selected="true"]': {
    outline: '2px solid #4529de',
    outlineOffset: '2px',
  },
  // A bound (auto-filled) field: a small chip naming its data source, top-right of the field.
  '& .formarea [data-custom-report-bound]': {
    position: 'relative',
  },
  '& .formarea [data-custom-report-bound]::after': {
    content: 'attr(data-custom-report-bound)',
    position: 'absolute',
    top: '2px',
    right: '2px',
    zIndex: 3,
    background: '#4529de',
    color: '#fff',
    borderRadius: '4px',
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: 1.4,
    pointerEvents: 'none',
    boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
  },
  // Field action buttons (edit / copy / remove) as a compact dark pill toolbar on the field.
  '& .formarea .component-btn-group': {
    background: '#201f27',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '9px',
    boxShadow: '0 10px 24px rgba(0,0,0,0.40)',
    display: 'inline-flex',
    gap: '2px',
    padding: '3px',
  },
  '& .formarea .component-btn-group .btn': {
    background: 'transparent',
    border: 0,
    borderRadius: '6px',
    boxShadow: 'none',
    color: '#cfcdd8',
    minHeight: '26px',
    minWidth: '26px',
    padding: '4px 6px',
  },
  '& .formarea .component-btn-group .btn:hover': {
    background: 'rgba(255,255,255,0.10)',
    color: '#fff',
  },
  '& .formarea .component-btn-group .btn.btn-danger:hover': {
    background: '#c24141',
    color: '#fff',
  },
  // Hide the per-field "edit" (settings) and "edit JSON" buttons. Fields are renamed inline; layout
  // components open the dialog on double-click (we click this hidden button programmatically). Removing
  // JSON editing also closes an injection surface.
  [[
    '& .formarea .component-btn-group [ref="editComponent"]',
    '& .formarea .component-btn-group .component-settings-button-edit',
    '& .formarea .component-btn-group [ref="editJson"]',
    '& .formarea .component-btn-group .component-settings-button-edit-json',
  ].join(', ')]: {
    display: 'none',
  },
  // Double-click a field label to rename it inline (cursor hints it's editable).
  '& .formarea .builder-component .col-form-label, & .formarea .builder-component .control-label, & .formarea .builder-component > .formio-component > label': {
    cursor: 'text',
  },
  // Text block (Content): give an empty block a clickable area, and show the inline colour toolbar.
  '& .formarea .formio-component-content [ref="content"]': {
    cursor: 'text',
    minHeight: '28px',
  },
  '& .formarea .formio-component-content [ref="content"][contenteditable="true"]': {
    borderRadius: '4px',
    boxShadow: '0 0 0 2px #4529de',
    outline: 'none',
    padding: '4px 6px',
  },
  '& .formarea .custom-report-content-toolbar': {
    alignItems: 'center',
    background: '#201f27',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '9px',
    boxShadow: '0 10px 24px rgba(0,0,0,0.40)',
    display: 'flex',
    gap: '3px',
    left: 0,
    padding: '4px 5px',
    position: 'absolute',
    top: '-38px',
    zIndex: 9,
  },
  '& .formarea .custom-report-content-toolbar .crc-swatch': {
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '4px',
    cursor: 'pointer',
    height: '16px',
    padding: 0,
    width: '16px',
  },
  '& .formarea .custom-report-content-toolbar .crc-done': {
    background: '#4529de',
    border: 0,
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 700,
    marginLeft: '4px',
    padding: '5px 9px',
  },
  // Form.io's screen-reader-only labels (e.g. "numeric only") should be visually hidden, not shown.
  '& .visually-hidden, & .sr-only': {
    border: 0,
    clip: 'rect(0, 0, 0, 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },
  // Coloured rich-text value that stands in for a Text Field / Text Area's plain input — matches the
  // field wells it replaces so a coloured field doesn't stand out as a different surface.
  '& .formarea .crt-display': {
    background: CANVAS_FIELD,
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '8px',
    color: CANVAS_INK,
    cursor: 'text',
    minHeight: '34px',
    padding: '7px 9px',
    whiteSpace: 'pre-wrap',
  },
  '& .formarea .custom-report-inline-label': {
    background: '#201f26',
    border: '1px solid #4529de',
    borderRadius: '4px',
    boxShadow: '0 0 0 3px rgba(69, 41, 222, 0.15)',
    color: CANVAS_INK,
    font: 'inherit',
    fontSize: '12.5px',
    fontWeight: 700,
    marginBottom: '6px',
    minWidth: '200px',
    outline: 'none',
    padding: '3px 7px',
  },
  // Select fields: a taller control and a clearly readable dropdown so authors can see the options they
  // added (choices.js renders these).
  '& .formarea .choices, & .formarea .choices__inner': {
    minHeight: '40px',
    width: '100%',
  },
  '& .formarea .choices__inner': {
    alignItems: 'center',
    background: CANVAS_FIELD,
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '8px',
    display: 'flex',
    fontSize: '14px',
    padding: '8px 32px 8px 11px',
  },
  // The open dropdown is an overlay, so it lifts slightly above the field it sits over.
  '& .formarea .choices__list--dropdown, & .formarea .choices__list[aria-expanded="true"]': {
    background: '#3a3838',
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '6px',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.45)',
    marginTop: '2px',
    zIndex: 30,
  },
  '& .formarea .choices__list--dropdown .choices__item': {
    color: CANVAS_INK,
    fontSize: '14px',
    padding: '9px 12px',
  },
  '& .formarea .choices__list--dropdown .choices__item--selectable.is-highlighted': {
    background: 'rgba(109, 92, 255, 0.24)',
    color: '#fff',
  },
  [builderResizableInputSelector]: {
    maxWidth: '100%',
    width: '100%',
  },
  [formioButtonSelector]: {
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    color: '#374151',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: '13px',
    fontWeight: 600,
    gap: '6px',
    justifyContent: 'center',
    minHeight: '36px',
    padding: '8px 14px',
    transition: 'background-color .1s ease, border-color .1s ease, color .1s ease',
  },
  [formioButtonHoverSelector]: {
    backgroundColor: '#eef2ff',
    borderColor: '#c7d2fe',
    color: '#6d5cff',
  },
  [formioPrimaryButtonSelector]: {
    backgroundColor: '#4529de',
    borderColor: '#4529de',
    color: '#fff',
  },
  [formioDangerButtonSelector]: {
    backgroundColor: '#c24141',
    borderColor: '#c24141',
    color: '#fff',
  },
  [formioInputSelector]: {
    backgroundColor: CANVAS_FIELD,
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '8px',
    color: CANVAS_INK,
    minHeight: '36px',
    transition: 'border-color .1s ease, box-shadow .1s ease',
  },
  '& input:focus, & textarea:focus, & select:focus, & .form-control:focus': {
    borderColor: '#4529de',
    boxShadow: '0 0 0 3px rgba(69, 41, 222, 0.15)',
    outline: 'none',
  },
  // The canvas field "wells". These must be scoped to .formarea .formio-component: the builder markup has
  // no .formio-builder ancestor, so the selector above never reaches the canvas, and Form.io's bundled
  // Bootstrap (.form-control) paints them white unless we out-specify it.
  [builderCanvasFieldSelector]: {
    backgroundColor: CANVAS_FIELD,
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '8px',
    color: CANVAS_INK,
    minHeight: '36px',
  },
  [builderCanvasFieldFocusSelector]: {
    backgroundColor: CANVAS_FIELD,
    borderColor: '#4529de',
    boxShadow: '0 0 0 3px rgba(69, 41, 222, 0.15)',
    outline: 'none',
  },
  '& label, & legend, & .control-label, & .col-form-label': {
    color: CANVAS_LABEL,
  },
  '& .builder-sidebar_search input': {
    backgroundColor: '#242222',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '8px',
    color: '#e5e7eb',
    marginBottom: '10px',
    padding: '7px 9px',
    width: '100%',
  },
  '& .builder-sidebar_search input::placeholder': {
    color: '#8b8b90',
  },
  '& .builder-group-button, & .form-builder-group-header, & .formio-builder-group-header': {
    backgroundColor: '#403d3d',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '8px',
    color: '#e5e7eb',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    margin: '8px 0 6px',
    padding: '9px 12px',
    textTransform: 'uppercase',
    width: '100%',
  },
  '& .alert, & .drag-and-drop-alert': {
    backgroundColor: 'rgba(69, 41, 222, 0.14)',
    border: '1.5px dashed rgba(109, 92, 255, 0.55)',
    borderRadius: '10px',
    color: '#b9b0ff',
  },
};

// The fill view mirrors the builder canvas (same tokens) so the crew fills the report the author built.
// White paper lives in printRendererShellSx, which the fill dialog swaps in only for the PDF capture.
const rendererShellSx = {
  ...baseEditorShellSx,
  bgcolor: CANVAS_PAPER,
  borderColor: CANVAS_EDGE,
  color: CANVAS_INK,
  p: 2,
  ...bootstrapGridSx('.formio-form'),
  '& .formio-component-table td .formio-component > label:not(.form-check-label), & .formio-component-table td .formio-component > .col-form-label': {
    display: 'none',
  },
  '&, & .formio-form, & .formio-component, & .card, & .card-body, & .form-group, & .mb-3': {
    backgroundColor: CANVAS_PAPER,
    color: CANVAS_INK,
  },
  '& .formio-form': {
    padding: 0,
  },
  '& .formio-component': {
    marginBottom: '14px',
  },
  '& .formio-component-submit, & .formio-component-button': {
    display: 'none',
  },
  '& label, & legend, & .control-label, & .col-form-label, & .form-check-label': {
    color: CANVAS_LABEL,
    display: 'block',
    fontSize: '13px',
    fontWeight: 700,
    marginBottom: '5px',
  },
  '& input, & textarea, & select, & .form-control, & .form-select, & .choices__inner': {
    backgroundColor: CANVAS_FIELD,
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '8px',
    boxSizing: 'border-box',
    color: CANVAS_INK,
    minHeight: '34px',
    padding: '6px 8px',
  },
  '& input:focus, & textarea:focus, & select:focus, & .form-control:focus': {
    borderColor: '#4529de',
    boxShadow: '0 0 0 3px rgba(69, 41, 222, 0.15)',
    outline: 'none',
  },
  '& .choices__list--dropdown, & .choices__list[aria-expanded="true"]': {
    background: '#3a3838',
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '6px',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.45)',
  },
  '& .choices__list--dropdown .choices__item': {
    color: CANVAS_INK,
  },
  '& .choices__list--dropdown .choices__item--selectable.is-highlighted': {
    background: 'rgba(109, 92, 255, 0.24)',
    color: '#fff',
  },
  '& textarea': {
    minHeight: '72px',
  },
  '& input[type="checkbox"], & input[type="radio"]': {
    accentColor: '#4529de',
    height: 16,
    minHeight: 0,
    padding: 0,
    width: 16,
  },
  '& .formio-component[data-custom-report-resizable="true"]': {
    maxWidth: '100%',
    minHeight: 52,
    minWidth: 140,
    overflow: 'auto',
    paddingRight: '4px',
    resize: 'both',
  },
  [rendererResizableInputSelector]: {
    maxWidth: '100%',
    width: '100%',
  },
  '& .crt-display': {
    background: CANVAS_FIELD,
    border: `1px solid ${CANVAS_FIELD_BORDER}`,
    borderRadius: '8px',
    color: CANVAS_INK,
    minHeight: '34px',
    padding: '6px 8px',
    whiteSpace: 'pre-wrap',
  },
  '& .visually-hidden, & .sr-only': {
    border: 0,
    clip: 'rect(0, 0, 0, 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },
};

const printRendererShellSx = {
  ...baseEditorShellSx,
  bgcolor: '#fff',
  borderColor: '#cbd5e1',
  color: '#111827',
  p: 2,
  ...bootstrapGridSx('.formio-form'),
  '& .formio-component-table td .formio-component > label:not(.form-check-label), & .formio-component-table td .formio-component > .col-form-label': {
    display: 'none',
  },
  '&, & .formio-form, & .formio-component, & .card, & .card-body, & .form-group, & .mb-3': {
    backgroundColor: '#fff',
    color: '#111827',
  },
  '& .formio-form': {
    padding: 0,
  },
  '& .formio-component': {
    marginBottom: '14px',
  },
  '& .formio-component[data-custom-report-resizable="true"]': {
    maxWidth: '100%',
    minHeight: 52,
    minWidth: 140,
    overflow: 'auto',
    paddingRight: '4px',
    resize: 'both',
  },
  '& label, & legend, & .control-label, & .col-form-label, & .form-check-label': {
    color: '#111827',
    display: 'block',
    fontSize: '13px',
    fontWeight: 700,
    marginBottom: '5px',
  },
  '& input, & textarea, & select, & .form-control, & .form-select, & .choices__inner': {
    backgroundColor: '#fff',
    border: '1px solid #6b7280',
    borderRadius: '2px',
    boxSizing: 'border-box',
    color: '#111827',
    minHeight: '34px',
    padding: '6px 8px',
  },
  // Coloured rich-text value (read-only) shown in place of a plain input in the PDF/print view.
  '& .crt-display': {
    background: '#fff',
    border: '1px solid #6b7280',
    borderRadius: '2px',
    color: '#111827',
    minHeight: '34px',
    padding: '6px 8px',
    whiteSpace: 'pre-wrap',
  },
  '& .visually-hidden, & .sr-only': {
    border: 0,
    clip: 'rect(0, 0, 0, 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },
  [rendererResizableInputSelector]: {
    maxWidth: '100%',
    width: '100%',
  },
  '& textarea': {
    minHeight: '72px',
  },
  '& input[type="checkbox"], & input[type="radio"]': {
    accentColor: '#4529de',
    height: 16,
    minHeight: 0,
    padding: 0,
    width: 16,
  },
  '& .form-check': {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
  },
  '& .form-check-input': {
    margin: 0,
  },
  '& .formio-component-submit, & .formio-component-button': {
    display: 'none',
  },
  // Datagrids (Pours / Route Points / Approvers) are too wide for A4 as horizontal tables and get
  // clipped on the right. In the printed PDF, stack each row vertically as "Column label: value" blocks.
  // The column labels come from data-label (stamped by stampDatagridLabels before capture).
  '& .formio-component-datagrid table': {
    display: 'block',
    width: '100%',
    border: 'none',
  },
  '& .formio-component-datagrid thead': {
    display: 'none',
  },
  '& .formio-component-datagrid tbody': {
    display: 'block',
    width: '100%',
  },
  '& .formio-component-datagrid tbody tr': {
    display: 'block',
    width: '100%',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    padding: '6px 10px',
    marginBottom: '8px',
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  },
  '& .formio-component-datagrid tbody td': {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    width: '100%',
    border: 'none',
    padding: '2px 0',
  },
  '& .formio-component-datagrid tbody td::before': {
    color: '#111827',
    content: 'attr(data-label)',
    flex: '0 0 170px',
    fontSize: '12px',
    fontWeight: 700,
  },
  // The remove-button column (and any unlabeled cell) has no header — drop it so it adds no blank line.
  '& .formio-component-datagrid tbody td[data-label=""]': {
    display: 'none',
  },
  // Show values as plain text (no input boxes) in the finished document.
  '& .formio-component-datagrid tbody td :is(.form-control, input, textarea, select)': {
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    flex: 1,
    minHeight: 0,
    padding: 0,
  },
  // The "Add Another" / remove-row controls aren't part of the printed document.
  '& .formio-component-datagrid button': {
    display: 'none',
  },
};

const formioDialogGlobalStyles = {
  '.formio-dialog': {
    backgroundColor: 'rgba(10, 9, 16, 0.55)',
    bottom: 0,
    boxSizing: 'border-box',
    color: '#edecf2',
    fontFamily: 'inherit',
    left: 0,
    overflowY: 'auto',
    padding: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 1600,
  },
  '.formio-dialog, .formio-dialog .formio-form, .formio-dialog .formio-component': {
    color: '#edecf2',
    fontFamily: 'inherit',
  },
  // The theme-default variant is listed explicitly: Form.io's own stylesheet paints the content pane
  // light grey with a 3-class selector, which out-specifies a plain `.formio-dialog .formio-dialog-content`
  // — that was the white drawer.
  '.formio-dialog .card, .formio-dialog .card-body, .formio-dialog .formio-dialog-content, .formio-dialog.formio-dialog-theme-default .formio-dialog-content': {
    backgroundColor: '#2a2831',
    color: '#edecf2',
  },
  // Field settings render as a right-side drawer (not a centred modal), so the canvas stays visible.
  '.formio-dialog .formio-dialog-content': {
    borderLeft: '1px solid rgba(255,255,255,0.10)',
    borderRadius: '16px 0 0 16px',
    boxShadow: '-24px 0 60px rgba(0, 0, 0, 0.45)',
    boxSizing: 'border-box',
    height: '100vh',
    margin: '0 0 0 auto',
    maxHeight: '100vh',
    maxWidth: '440px',
    minHeight: 0,
    overflow: 'hidden',
    padding: '0',
    position: 'relative',
    width: 'min(440px, 100vw)',
  },
  '.formio-dialog .component-edit-container, .formio-dialog .formio-dialog-content > .formio-form': {
    maxHeight: '100vh',
    overflowY: 'auto',
    padding: '20px 22px',
  },
  // In the narrow drawer, stack the settings and the live preview instead of side-by-side columns.
  '.formio-dialog .component-edit-container .row > [class*="col-"]': {
    flex: '1 1 100%',
    maxWidth: '100%',
  },
  '.formio-dialog .formio-dialog-close, .formio-dialog .formio-dialog-close button': {
    background: 'transparent',
    border: 0,
    color: '#6b7280',
    cursor: 'pointer',
    fontSize: '24px',
    lineHeight: 1,
    minHeight: 'auto',
    padding: '6px',
    position: 'absolute',
    right: '14px',
    top: '10px',
  },
  '.formio-dialog h1, .formio-dialog h2, .formio-dialog h3, .formio-dialog h4': {
    color: '#f4f3f8',
    fontSize: '16px',
    fontWeight: 700,
    margin: '0 36px 16px 0',
  },
  '.formio-dialog ul.nav, .formio-dialog .nav-tabs': {
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px',
    listStyle: 'none',
    margin: '0 0 18px',
    padding: 0,
  },
  '.formio-dialog .nav-item, .formio-dialog .nav-tabs > li': {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  '.formio-dialog .nav-link, .formio-dialog .nav-tabs > li > a': {
    background: 'transparent',
    border: 0,
    borderBottom: '2px solid transparent',
    borderRadius: 0,
    color: '#6b7280',
    display: 'inline-flex',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.3,
    marginBottom: '-1px',
    padding: '9px 14px',
    textDecoration: 'none',
    transition: 'color .12s ease, border-color .12s ease',
  },
  '.formio-dialog .nav-link:hover, .formio-dialog .nav-tabs > li > a:hover': {
    color: '#6d5cff',
  },
  '.formio-dialog .nav-link.active, .formio-dialog .nav-tabs > li.active > a': {
    background: 'transparent',
    borderBottomColor: '#4529de',
    color: '#6d5cff',
    fontWeight: 700,
  },
  '.formio-dialog .tab-content': {
    display: 'block',
  },
  '.formio-dialog .tab-pane:not(.active)': {
    display: 'none',
  },
  '.formio-dialog .tab-pane.active': {
    display: 'block',
  },
  '.formio-dialog .row': {
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: '-6px',
    marginRight: '-6px',
  },
  '.formio-dialog [class*="col-"]': {
    boxSizing: 'border-box',
    flex: '1 1 260px',
    maxWidth: '100%',
    minWidth: 0,
    paddingLeft: '6px',
    paddingRight: '6px',
  },
  '.formio-dialog .col-sm-2, .formio-dialog .col-md-2': {
    flexBasis: '16.666%',
  },
  '.formio-dialog .col-sm-3, .formio-dialog .col-md-3': {
    flexBasis: '25%',
  },
  '.formio-dialog .col-sm-4, .formio-dialog .col-md-4': {
    flexBasis: '33.333%',
  },
  '.formio-dialog .col-sm-6, .formio-dialog .col-md-6': {
    flexBasis: '50%',
  },
  '.formio-dialog .col-sm-8, .formio-dialog .col-md-8': {
    flexBasis: '66.666%',
  },
  '.formio-dialog .col-sm-9, .formio-dialog .col-md-9': {
    flexBasis: '75%',
  },
  '.formio-dialog .col-sm-12, .formio-dialog .col-md-12': {
    flexBasis: '100%',
  },
  '.formio-dialog .form-group, .formio-dialog .mb-3, .formio-dialog .formio-component': {
    marginBottom: '14px',
  },
  '.formio-dialog label, .formio-dialog legend, .formio-dialog .control-label, .formio-dialog .col-form-label': {
    display: 'block',
    fontSize: '12.5px',
    fontWeight: 700,
    marginBottom: '5px',
    color: '#d7d6de',
  },
  '.formio-dialog input, .formio-dialog textarea, .formio-dialog select, .formio-dialog .form-control, .formio-dialog .form-select': {
    backgroundColor: '#201f26',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '8px',
    boxSizing: 'border-box',
    color: '#edecf2',
    display: 'block',
    fontSize: '14px',
    minHeight: '38px',
    padding: '8px 11px',
    transition: 'border-color .1s ease, box-shadow .1s ease',
    width: '100%',
  },
  '.formio-dialog input:focus, .formio-dialog textarea:focus, .formio-dialog select:focus, .formio-dialog .form-control:focus, .formio-dialog .form-select:focus': {
    borderColor: '#4529de',
    boxShadow: '0 0 0 3px rgba(69, 41, 222, 0.15)',
    outline: 'none',
  },
  '.formio-dialog textarea': {
    minHeight: '74px',
    resize: 'vertical',
  },
  '.formio-dialog .choices': {
    boxSizing: 'border-box',
    marginBottom: 0,
    position: 'relative',
    width: '100%',
  },
  '.formio-dialog .choices__inner': {
    alignItems: 'center',
    backgroundColor: '#201f26',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '8px',
    boxSizing: 'border-box',
    color: '#edecf2',
    display: 'flex',
    minHeight: '38px',
    padding: '6px 32px 6px 11px',
  },
  '.formio-dialog .choices__list--single': {
    display: 'block',
    padding: 0,
    width: '100%',
  },
  '.formio-dialog .choices__button': {
    background: 'transparent',
    border: 0,
    color: '#6b7280',
    minHeight: 'auto',
    padding: '0 6px',
  },
  '.formio-dialog .ace_editor, .formio-dialog .CodeMirror': {
    background: '#201f26 !important',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '8px',
    boxSizing: 'border-box',
    color: '#edecf2 !important',
    minHeight: '74px',
    width: '100%',
  },
  '.formio-dialog .ace_gutter, .formio-dialog .ace_gutter-active-line': {
    background: '#26242c !important',
    color: '#7c7a89 !important',
  },
  '.formio-dialog .ace_scroller, .formio-dialog .ace_content, .formio-dialog .ace_text-layer, .formio-dialog .ace_layer': {
    background: '#201f26 !important',
    color: '#edecf2 !important',
  },
  // Dark checkboxes/radios and tidy tables so layout dialogs (Columns / Table) match the left panel.
  '.formio-dialog input[type="checkbox"], .formio-dialog input[type="radio"]': {
    accentColor: '#4529de',
    height: '16px',
    minHeight: 0,
    width: '16px',
  },
  '.formio-dialog table': {
    borderColor: 'rgba(255,255,255,0.10)',
    width: '100%',
  },
  '.formio-dialog th, .formio-dialog td': {
    borderColor: 'rgba(255,255,255,0.10) !important',
    color: '#d7d6de',
    padding: '7px 9px',
    verticalAlign: 'middle',
  },
  // JSON editing is disabled everywhere (injection surface).
  '.formio-dialog [ref="editJson"], .formio-dialog .component-settings-button-edit-json, .formio-dialog .btn-edit-json': {
    display: 'none',
  },
  // Screen-reader-only hints ("numeric only") — visually hidden in the dialog preview, as on the canvas.
  '.formio-dialog .visually-hidden, .formio-dialog .sr-only': {
    border: 0,
    clip: 'rect(0, 0, 0, 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  },
  [hiddenFormioDisplaySettingsSelector]: {
    display: 'none',
  },
  [hiddenFormioEditTabsSelector]: {
    display: 'none',
  },
  // The edit dialog is flattened by flattenComponentEditDialog(): the Data and Validation panes are
  // stacked under Display, so give them a light divider + heading instead of a tab label.
  '.formio-dialog [data-custom-report-edit-pane="data"], .formio-dialog [data-custom-report-edit-pane="validation"]': {
    borderTop: '1px solid rgba(255,255,255,0.10)',
    marginTop: '6px',
    paddingTop: '16px',
  },
  '.formio-dialog [data-custom-report-edit-pane="data"]::before': {
    color: '#9a98a6',
    content: '"Field options"',
    display: 'block',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginBottom: '12px',
    textTransform: 'uppercase',
  },
  '.formio-dialog [data-custom-report-edit-pane="validation"]::before': {
    color: '#9a98a6',
    content: '"Validation"',
    display: 'block',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginBottom: '12px',
    textTransform: 'uppercase',
  },
  '.formio-dialog button, .formio-dialog .btn': {
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: '8px',
    color: '#d7d6de',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: '13px',
    fontWeight: 600,
    gap: '6px',
    justifyContent: 'center',
    minHeight: '36px',
    padding: '8px 14px',
    transition: 'background-color .1s ease, border-color .1s ease, color .1s ease',
  },
  '.formio-dialog button:hover, .formio-dialog .btn:hover': {
    backgroundColor: 'rgba(69,41,222,0.18)',
    borderColor: '#4529de',
    color: '#b7abff',
  },
  '.formio-dialog .btn-primary, .formio-dialog button.btn-primary': {
    backgroundColor: '#4529de',
    borderColor: '#4529de',
    color: '#fff',
  },
  '.formio-dialog .btn-primary:hover, .formio-dialog button.btn-primary:hover': {
    backgroundColor: '#3a1fc4',
    borderColor: '#3a1fc4',
    color: '#fff',
  },
  '.formio-dialog .btn-danger, .formio-dialog button.btn-danger': {
    backgroundColor: '#c24141',
    borderColor: '#c24141',
    color: '#fff',
  },
  '.formio-dialog .btn-danger:hover, .formio-dialog button.btn-danger:hover': {
    backgroundColor: '#a83636',
    borderColor: '#a83636',
    color: '#fff',
  },
  '.formio-dialog .formio-dialog-buttons, .formio-dialog .modal-footer': {
    alignItems: 'center',
    backgroundColor: '#2a2831',
    borderTop: '1px solid rgba(255,255,255,0.10)',
    bottom: 0,
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    marginTop: '16px',
    padding: '12px 20px',
    position: 'sticky',
  },
  '@media (max-width: 560px)': {
    '.formio-dialog .formio-dialog-content': {
      borderRadius: 0,
      maxWidth: '100vw',
      width: '100vw',
    },
  },
};

interface LogoPosition { xPct: number; yPct: number }

const DEFAULT_LOGO_POSITION: LogoPosition = { xPct: 2, yPct: 2 };

const clampPct = (value: number) => Math.max(0, Math.min(96, value));

const logoPositionFromSchema = (schema?: CustomReportFormSchemaDto): LogoPosition => {
  const raw = schema?.logoPosition as Partial<LogoPosition> | undefined;
  if (raw && typeof raw.xPct === 'number' && typeof raw.yPct === 'number') {
    return { xPct: clampPct(raw.xPct), yPct: clampPct(raw.yPct) };
  }
  return DEFAULT_LOGO_POSITION;
};

const logoWidthFromSchema = (schema?: CustomReportFormSchemaDto): number | undefined => {
  const raw = schema?.logoWidth;
  return typeof raw === 'number' && raw > 0 ? raw : undefined;
};

// Manages the report logo (client default + in-session upload).
const useReportLogo = (defaultLogoDataUri?: string, onLogoUploaded?: (logoDataUri: string) => Promise<void> | void) => {
  const [uploadedLogoDataUri, setUploadedLogoDataUri] = React.useState<string>();
  const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);
  const logoDataUri = uploadedLogoDataUri || defaultLogoDataUri;

  const uploadLogo = async (file?: File) => {
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      const logo = await readLogoFileAsDataUri(file);
      setUploadedLogoDataUri(logo);
      try {
        await onLogoUploaded?.(logo);
      } catch {
        // Keep the editor usable if the client logo update fails.
      }
    } finally {
      setIsUploadingLogo(false);
    }
  };

  return { logoDataUri, uploadLogo, isUploadingLogo };
};

const CustomReportLogoUploadButton = ({
  hasLogo,
  isUploading,
  onUpload,
}: {
  hasLogo: boolean;
  isUploading: boolean;
  onUpload: (file?: File) => void;
}) => {
  const uploadLabel = hasLogo ? i18n._('Change logo') : i18n._('Add logo');

  return (
    <Button
      component="label"
      size="small"
      variant="outlined"
      color="secondary"
      startIcon={<UploadFileIcon />}
      disabled={isUploading}
    >
      {isUploading ? i18n._('Uploading...') : uploadLabel}
      <input
        hidden
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={(event) => {
          const input = event.currentTarget;
          onUpload(event.target.files?.[0]);
          input.value = '';
        }}
      />
    </Button>
  );
};

// The logo as a freely-draggable, absolutely-positioned element. Its parent must be position:relative.
// When `onPositionChange` is supplied (builder) the user can drag it anywhere; otherwise it's static
// (fill view / PDF), rendered at the stored position.
const CustomReportLogoLayer = ({
  logoDataUri,
  position,
  width,
  onPositionChange,
  onWidthChange,
}: {
  logoDataUri?: string;
  position: LogoPosition;
  width?: number;
  onPositionChange?: (position: LogoPosition) => void;
  onWidthChange?: (width: number) => void;
}) => {
  if (!logoDataUri) return null;
  const editable = Boolean(onPositionChange);

  // Pull the corner handle to resize the logo (clamped to fit the band).
  const handleResizeDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!onWidthChange) return;
    event.preventDefault();
    event.stopPropagation();
    const layer = event.currentTarget.parentElement as HTMLElement | null;
    const image = layer?.querySelector('img');
    const parent = layer?.offsetParent as HTMLElement | null;
    if (!layer || !image || !parent) return;

    const imageRect = image.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const startX = event.clientX;
    const startWidth = imageRect.width;
    const aspect = imageRect.width > 0 ? imageRect.height / imageRect.width : 0.35;
    const maxWidth = Math.min(parentRect.width - 12, aspect > 0 ? (parentRect.height - 12) / aspect : parentRect.width);

    const handleMove = (moveEvent: PointerEvent) => {
      const next = Math.max(60, Math.min(maxWidth, startWidth + (moveEvent.clientX - startX)));
      onWidthChange(Math.round(next));
    };
    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!onPositionChange) return;
    event.preventDefault();
    const logoEl = event.currentTarget;
    const parent = logoEl.offsetParent as HTMLElement | null;
    if (!parent) return;

    const parentRect = parent.getBoundingClientRect();
    const logoRect = logoEl.getBoundingClientRect();
    const grabX = event.clientX - logoRect.left;
    const grabY = event.clientY - logoRect.top;
    // Keep the logo fully inside its band.
    const maxXPct = parentRect.width > logoRect.width ? ((parentRect.width - logoRect.width) / parentRect.width) * 100 : 0;
    const maxYPct = parentRect.height > logoRect.height ? ((parentRect.height - logoRect.height) / parentRect.height) * 100 : 0;

    const handleMove = (moveEvent: PointerEvent) => {
      const x = moveEvent.clientX - parentRect.left - grabX;
      const y = moveEvent.clientY - parentRect.top - grabY;
      onPositionChange({
        xPct: Math.max(0, Math.min(maxXPct, (x / parentRect.width) * 100)),
        yPct: Math.max(0, Math.min(maxYPct, (y / parentRect.height) * 100)),
      });
    };
    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  return (
    <Box
      onPointerDown={handlePointerDown}
      sx={{
        // A white plate sized to the artwork: logos are usually dark, so they need a light ground to stay
        // legible on the dark canvas. On white paper it's white-on-white, so the PDF is unaffected.
        backgroundColor: '#fff',
        borderRadius: '6px',
        cursor: editable ? 'move' : 'default',
        left: `${position.xPct}%`,
        p: 0.75,
        position: 'absolute',
        top: `${position.yPct}%`,
        touchAction: 'none',
        userSelect: 'none',
        zIndex: 6,
        ...(editable ? {
          outline: '1px dashed rgba(69, 41, 222,0.5)',
          outlineOffset: 3,
          '&:hover': { outlineColor: '#6d5cff' },
        } : {}),
      }}
    >
      <Box
        component="img"
        src={logoDataUri}
        alt="Logo"
        draggable={false}
        sx={{
          display: 'block',
          height: 'auto',
          maxHeight: width ? 'none' : 60,
          maxWidth: width ? 'none' : 220,
          objectFit: 'contain',
          pointerEvents: 'none',
          width: width ? `${width}px` : 'auto',
        }}
      />
      {onWidthChange && (
        <Box
          onPointerDown={handleResizeDown}
          sx={{
            backgroundColor: '#4529de',
            border: '2px solid #fff',
            borderRadius: '2px',
            bottom: -6,
            cursor: 'nwse-resize',
            height: 12,
            position: 'absolute',
            right: -6,
            width: 12,
            zIndex: 8,
          }}
        />
      )}
    </Box>
  );
};

// The "letterhead" band at the top of the document that holds the (draggable, resizable) logo. It's a
// plain React element — NOT part of Form.io's canvas — so dragging fields never disturbs it. `action` is
// the change-logo control shown in the builder.
// The band itself is transparent: it's only a positioning area, so it blends into whatever canvas is
// behind it (dark while building/filling, white paper in the PDF) with no theme branching. The logo keeps
// its own white plate — see CustomReportLogoLayer.
const CustomReportLogoBand = ({
  logoDataUri,
  position,
  width,
  onPositionChange,
  onWidthChange,
  action,
  sx,
}: {
  logoDataUri?: string;
  position: LogoPosition;
  width?: number;
  onPositionChange?: (position: LogoPosition) => void;
  onWidthChange?: (width: number) => void;
  action?: React.ReactNode;
  sx?: SxProps<Theme>;
}) => (
  <Box
    sx={{
      minHeight: 96,
      overflow: 'hidden',
      position: 'relative',
      ...sx,
    }}
  >
    <CustomReportLogoLayer
      logoDataUri={logoDataUri}
      position={position}
      width={width}
      onPositionChange={onPositionChange}
      onWidthChange={onWidthChange}
    />
    {action && (
      <Box sx={{ position: 'absolute', right: 6, top: 6, zIndex: 7 }}>{action}</Box>
    )}
  </Box>
);

// Strip Form.io-internal ids (and our own transient size hints) from a saved schema before feeding it
// back into the builder. Stale component ids from a previous session can leave a reopened form's
// components non-draggable; regenerating them keeps edit behaving exactly like a fresh build.
const stripBuilderInternals = (component: CustomReportFormComponentDto): CustomReportFormComponentDto => {
  const next: CustomReportFormComponentDto = { ...component };
  delete (next as Record<string, unknown>).id;
  delete (next as Record<string, unknown>).customReportWidth;
  delete (next as Record<string, unknown>).customReportHeight;

  if (Array.isArray(next.components)) {
    next.components = next.components.map(stripBuilderInternals);
  }
  if (Array.isArray(next.columns)) {
    next.columns = next.columns.map((column) => ({
      ...column,
      components: (column.components ?? []).map(stripBuilderInternals),
    }));
  }
  if (Array.isArray(next.rows)) {
    next.rows = next.rows.map((row) => (Array.isArray(row)
      ? row.map((cell) => ({ ...cell, components: (cell.components ?? []).map(stripBuilderInternals) }))
      : row));
  }

  return next;
};

const sanitiseSchemaForBuilder = (schema?: CustomReportFormSchemaDto): CustomReportFormSchemaDto => {
  const normalised = normaliseCustomReportFormSchema(schema);
  return { ...normalised, components: normalised.components.map(stripBuilderInternals) };
};

// Given a click inside the builder canvas, resolve which field (schema key) was clicked.
const selectedComponentKeyFrom = (target: HTMLElement | null, schema: CustomReportFormSchemaDto): string | undefined => {
  const element = target?.closest<HTMLElement>('.formio-component');
  if (!element) return undefined;
  const keys = collectCustomReportFormComponents(schema.components)
    .map((component) => component.key)
    .filter((key): key is string => Boolean(key));
  return keys.find((key) => element.classList.contains(`formio-component-${key}`));
};

export const CustomReportFormioBuilder = ({
  schema,
  onChange,
  defaultLogoDataUri,
  onLogoUploaded,
  bindingGroups,
}: CustomReportFormioBuilderProps) => {
  const builderRef = React.useRef<HTMLDivElement | null>(null);
  const settingsPanelRef = React.useRef<HTMLDivElement | null>(null);
  const onChangeRef = React.useRef(onChange);
  const initialSchemaRef = React.useRef(normaliseCustomReportFormSchema(schema));
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const selectedKeyRef = React.useRef<string | undefined>();
  const [selectedType, setSelectedType] = React.useState<string | undefined>();
  const [selectedKey, setSelectedKey] = React.useState<string | undefined>();
  const [selectedBinding, setSelectedBinding] = React.useState<string | undefined>();
  const [selectedLabelText, setSelectedLabelText] = React.useState('');
  const [selectedRequired, setSelectedRequired] = React.useState(false);
  const [optionRows, setOptionRows] = React.useState<Array<{ label: string; value: string }>>([]);
  const bindingLabelByKey = React.useMemo(() => {
    const map: Record<string, string> = {};
    (bindingGroups ?? []).forEach((group) => group.items?.forEach((item) => {
      if (item.key && item.label) map[item.key] = item.label;
    }));
    return map;
  }, [bindingGroups]);
  const bindingLabelByKeyRef = React.useRef(bindingLabelByKey);
  React.useEffect(() => { bindingLabelByKeyRef.current = bindingLabelByKey; }, [bindingLabelByKey]);
  const [labelColours, setLabelColours] = React.useState<Record<string, string>>(
    () => collectLabelColours(normaliseCustomReportFormSchema(schema)),
  );
  const { logoDataUri, uploadLogo, isUploadingLogo } = useReportLogo(defaultLogoDataUri, onLogoUploaded);
  const [logoPosition, setLogoPosition] = React.useState<LogoPosition>(() => logoPositionFromSchema(schema));
  const logoPositionRef = React.useRef(logoPosition);
  const [logoWidth, setLogoWidth] = React.useState<number | undefined>(() => logoWidthFromSchema(schema));
  const logoWidthRef = React.useRef(logoWidth);
  const builderInstanceRef = React.useRef<FormioInstance | undefined>(undefined);

  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Rename a field inline — double-click its label and type. This is the everyday edit; the full settings
  // dialog stays available on the field's gear button for choice options etc.
  const commitComponentLabel = (key: string, label: string) => {
    const nextSchema: CustomReportFormSchemaDto = {
      ...initialSchemaRef.current,
      components: updateComponents(initialSchemaRef.current.components, (component) => (
        component.key === key ? { ...component, label } : component
      )),
    };
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
    // Keep the docked settings panel in step when the rename came from the canvas.
    if (selectedKeyRef.current === key) setSelectedLabelText(label);
    // Push it into Form.io's own model so it isn't reverted on the next change.
    try {
      builderInstanceRef.current?.setForm?.(sanitiseSchemaForBuilder(nextSchema));
    } catch {
      // The schema is already our source of truth; a redraw hiccup won't lose the rename.
    }
  };

  // Required toggle for the selected field (docked settings panel).
  const applyRequired = (required: boolean) => {
    const key = selectedKeyRef.current;
    if (!key) return;
    setSelectedRequired(required);
    const nextSchema = withComponentRequired(initialSchemaRef.current, key, required);
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
    try {
      builderInstanceRef.current?.setForm?.(sanitiseSchemaForBuilder(nextSchema));
    } catch {
      // Schema is the source of truth; a redraw hiccup won't lose the flag.
    }
  };

  const startInlineLabelEdit = (component: HTMLElement, key: string) => {
    const labelEl = component.querySelector<HTMLElement>('.col-form-label, .control-label, label');
    if (!labelEl || component.querySelector('.custom-report-inline-label')) return;
    const current = collectCustomReportFormComponents(initialSchemaRef.current.components)
      .find((entry) => entry.key === key)?.label ?? labelEl.textContent?.trim() ?? '';

    const input = document.createElement('input');
    input.type = 'text';
    input.value = current;
    input.className = 'custom-report-inline-label';
    const previousDisplay = labelEl.style.display;
    labelEl.style.display = 'none';
    labelEl.insertAdjacentElement('afterend', input);
    input.focus();
    input.select();

    let finished = false;
    const finish = (save: boolean) => {
      if (finished) return;
      finished = true;
      const value = input.value.trim();
      input.remove();
      labelEl.style.display = previousDisplay;
      if (save && value && value !== current) commitComponentLabel(key, value);
    };
    input.addEventListener('blur', () => finish(true));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') { event.preventDefault(); finish(true); } else if (event.key === 'Escape') { event.preventDefault(); finish(false); }
    });
  };

  // Shared inline rich-text editor: makes `target` contentEditable with a floating colour toolbar
  // (pick a colour then type, or select words to recolour). Commits the HTML when the user clicks away.
  const startRichTextEdit = (component: HTMLElement, target: HTMLElement, onCommit: (html: string) => void) => {
    const componentEl = component;
    const targetEl = target;

    if (targetEl.dataset.customReportEditing === 'true') return;
    targetEl.dataset.customReportEditing = 'true';
    targetEl.contentEditable = 'true';
    targetEl.focus();

    const toolbar = document.createElement('div');
    toolbar.className = 'custom-report-content-toolbar';
    toolbar.contentEditable = 'false';
    let finished = false;
    let onOutside: (event: PointerEvent) => void = () => undefined;
    const finish = () => {
      if (finished) return;
      finished = true;
      document.removeEventListener('pointerdown', onOutside, true);
      toolbar.remove();
      targetEl.contentEditable = 'inherit';
      targetEl.dataset.customReportEditing = '';
      onCommit(targetEl.innerHTML);
    };
    onOutside = (event: PointerEvent) => {
      if (!componentEl.contains(event.target as Node)) finish();
    };

    LABEL_COLOURS.forEach((colour) => {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.title = colour;
      swatch.className = 'crc-swatch';
      swatch.style.background = colour;
      swatch.addEventListener('mousedown', (event) => event.preventDefault());
      swatch.addEventListener('click', () => { targetEl.focus(); document.execCommand('foreColor', false, colour); });
      toolbar.appendChild(swatch);
    });
    const doneButton = document.createElement('button');
    doneButton.type = 'button';
    doneButton.textContent = 'Done';
    doneButton.className = 'crc-done';
    doneButton.addEventListener('mousedown', (event) => event.preventDefault());
    doneButton.addEventListener('click', finish);
    toolbar.appendChild(doneButton);

    if (getComputedStyle(componentEl).position === 'static') componentEl.style.position = 'relative';
    componentEl.appendChild(toolbar);
    document.addEventListener('pointerdown', onOutside, true);
  };

  // Text block (Form.io Content).
  const commitContentHtml = (key: string, html: string) => {
    const nextSchema: CustomReportFormSchemaDto = {
      ...initialSchemaRef.current,
      components: updateComponents(initialSchemaRef.current.components, (component) => (
        component.key === key ? { ...component, html } : component
      )),
    };
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
    try {
      builderInstanceRef.current?.setForm?.(sanitiseSchemaForBuilder(nextSchema));
    } catch {
      // Schema is the source of truth; a redraw hiccup won't lose the text.
    }
  };

  const startContentEdit = (component: HTMLElement, key: string) => {
    const target = component.querySelector<HTMLElement>('[ref="content"]');
    if (target) startRichTextEdit(component, target, (html) => commitContentHtml(key, html));
  };

  // Text Field / Text Area value → coloured rich text, authored on the build canvas.
  const commitFieldRichText = (key: string, html: string) => {
    const nextSchema = withComponentRichText(initialSchemaRef.current, key, html);
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
    if (builderRef.current) applyStoredRichText(builderRef.current, nextSchema);
  };

  const startFieldRichEdit = (component: HTMLElement, key: string) => {
    const input = component.querySelector<HTMLInputElement | HTMLTextAreaElement>('input.form-control, textarea');
    if (!input) return;
    const model = collectCustomReportFormComponents(initialSchemaRef.current.components).find((entry) => entry.key === key);
    const html = (typeof model?.customReportRichText === 'string' && model.customReportRichText)
      || escapeHtmlText(input.value || '');
    let display = component.querySelector<HTMLElement>('.crt-display');
    if (!display) {
      display = document.createElement('div');
      display.className = 'form-control crt-display';
      input.style.display = 'none';
      input.insertAdjacentElement('afterend', display);
    }
    display.innerHTML = html;
    startRichTextEdit(component, display, (out) => commitFieldRichText(key, out));
  };

  const updateLogoPosition = (position: LogoPosition) => {
    logoPositionRef.current = position;
    setLogoPosition(position);
    const nextSchema: CustomReportFormSchemaDto = { ...initialSchemaRef.current, logoPosition: position };
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
  };

  const updateLogoWidth = (nextWidth: number) => {
    logoWidthRef.current = nextWidth;
    setLogoWidth(nextWidth);
    const nextSchema: CustomReportFormSchemaDto = { ...initialSchemaRef.current, logoWidth: nextWidth };
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
  };

  // Click a field on the canvas to select it (highlighted); the docked settings panel targets it.
  const selectField = (key?: string) => {
    selectedKeyRef.current = key;
    setSelectedKey(key);
    const component = key
      ? collectCustomReportFormComponents(initialSchemaRef.current.components).find((entry) => entry.key === key)
      : undefined;
    setSelectedType(component?.type);
    setSelectedBinding(component ? bindingKeyForComponent(component) : undefined);
    setSelectedLabelText(component?.label ?? '');
    setSelectedRequired(Boolean(component?.validate?.required));
    setOptionRows(component && CHOICE_TYPES.has(component.type) ? componentOptions(component) : []);
    const container = builderRef.current;
    if (!container) return;
    container.querySelectorAll('[data-custom-report-selected="true"]')
      .forEach((element) => element.removeAttribute('data-custom-report-selected'));
    if (key) formioComponentElementFor(container, key)?.setAttribute('data-custom-report-selected', 'true');
  };

  const applyLabelColour = (colour?: string) => {
    const key = selectedKeyRef.current;
    if (!key) return;
    const nextSchema = withComponentLabelColour(initialSchemaRef.current, key, colour);
    initialSchemaRef.current = nextSchema;
    setLabelColours(collectLabelColours(nextSchema));
    onChangeRef.current(nextSchema);
  };

  // Bind (or unbind) the selected field to a BFP data source. The badge and canvas re-render so the
  // author immediately sees which fields auto-fill.
  const applyBinding = (bindingKey?: string) => {
    const key = selectedKeyRef.current;
    if (!key) return;
    setSelectedBinding(bindingKey);
    const nextSchema = withComponentBinding(initialSchemaRef.current, key, bindingKey);
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
    if (builderRef.current) {
      applyBindingBadges(builderRef.current, nextSchema, (item) => bindingLabelByKeyRef.current[item] ?? item);
    }
  };

  // Choice-field options, edited inline. Each change re-renders the field via setForm so the canvas and
  // the saved schema stay in step.
  const commitOptions = (options: Array<{ label: string; value: string }>) => {
    const key = selectedKeyRef.current;
    if (!key) return;
    const nextSchema = withComponentOptions(initialSchemaRef.current, key, options);
    initialSchemaRef.current = nextSchema;
    onChangeRef.current(nextSchema);
    try {
      builderInstanceRef.current?.setForm?.(sanitiseSchemaForBuilder(nextSchema));
    } catch {
      // The schema is our source of truth; a redraw hiccup won't lose the options.
    }
  };

  const addOption = () => {
    const next = [...optionRows, { label: `Option ${optionRows.length + 1}`, value: '' }];
    setOptionRows(next);
    commitOptions(next);
  };
  const removeOption = (index: number) => {
    const next = optionRows.filter((_, i) => i !== index);
    setOptionRows(next);
    commitOptions(next);
  };
  const updateOptionLabel = (index: number, label: string) => {
    setOptionRows((rows) => rows.map((row, i) => (i === index ? { ...row, label } : row)));
  };

  // Docks the React-rendered field-settings panel INSIDE Form.io's palette sidebar, so components and
  // field settings read as one continuous panel. The panel's DOM node is simply re-parented — React
  // keeps updating it wherever it lives — and if Form.io ever re-renders the sidebar and drops it, the
  // next call re-docks it.
  const ensureSettingsDocked = () => {
    // Dock inside the same wrapper that holds the palette groups (.builder-sidebar_scroll), NOT the
    // outer .formcomponents: the scroll wrapper is position:sticky, so a sibling appended after it
    // overlaps the groups on short viewports instead of flowing below them.
    const sidebar = builderRef.current?.querySelector(
      '.formcomponents .builder-sidebar_scroll, .formcomponents .builder-sidebar, .formcomponents',
    );
    const panel = settingsPanelRef.current;
    if (sidebar && panel && panel.parentElement !== sidebar) sidebar.appendChild(panel);
  };

  React.useEffect(() => {
    if (!builderRef.current) return undefined;

    let active = true;
    let builder: FormioInstance | undefined;
    let fieldResize: { sync: () => void; detach: () => void } | undefined;
    const detachEditDialogFlattening = attachComponentEditDialogFlattening();

    // Double-click a field to rename its label inline; double-click a layout component to open its dialog.
    const container = builderRef.current;
    const handleDoubleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.component-btn-group')) return;
      const component = target?.closest<HTMLElement>('.builder-component');
      if (!component) return;
      const key = selectedComponentKeyFrom(target, initialSchemaRef.current);
      const model = key
        ? collectCustomReportFormComponents(initialSchemaRef.current.components).find((entry) => entry.key === key)
        : undefined;
      const isEditableField = model && model.input && !LAYOUT_TYPES.has(model.type);
      if (model?.type === 'content' && key) {
        // Text block → inline rich-text (type + colour selected words), like a Word paragraph.
        event.preventDefault();
        startContentEdit(component, key);
      } else if (model && RICH_TEXT_TYPES.has(model.type) && key) {
        // Text Field / Text Area: double-click the LABEL to rename, the value area to colour the text.
        event.preventDefault();
        if (target?.closest('.col-form-label, .control-label, label')) startInlineLabelEdit(component, key);
        else startFieldRichEdit(component, key);
      } else if (isEditableField && key) {
        event.preventDefault();
        startInlineLabelEdit(component, key);
      } else {
        // Layout component: open the settings dialog.
        component.querySelector<HTMLElement>('[ref="editComponent"], .component-settings-button-edit')?.click();
      }
    };
    container.addEventListener('dblclick', handleDoubleClick);

    // Single-click a field to select it for colouring (ignore clicks on Form.io's action buttons).
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.component-btn-group')) return;
      // Only canvas clicks drive selection: the palette AND the docked field-settings panel also live
      // inside this container, and a click there (e.g. opening the Bound data dropdown) must not clear
      // the selection out from under the panel — that unmounted the card and collapsed the dropdown.
      if (!target?.closest('.formarea')) return;
      selectField(selectedComponentKeyFrom(target, initialSchemaRef.current));
    };
    container.addEventListener('click', handleClick);

    setIsLoading(true);
    setHasError(false);

    loadFormio()
      .then((Formio) => {
        if (!active || !builderRef.current) return undefined;
        return Formio.builder(
          builderRef.current,
          sanitiseSchemaForBuilder(initialSchemaRef.current),
          { ...formioBuilderOptions, ...getFormioI18nOptions() },
        );
      })
      .then((instance?: FormioInstance) => {
        if (!instance) return;
        if (!active) {
          instance.destroy?.();
          return;
        }

        builder = instance;
        builderInstanceRef.current = instance;
        setIsLoading(false);
        // Force every palette group open (Form.io only expands the one marked `default`), so Layout shows
        // its components alongside Fields. `.accordion-collapse` is the collapsible element — the
        // `#group-container-*` inner grid is NOT it, and targeting that was why Layout stayed empty.
        container.querySelectorAll<HTMLElement>('.accordion-collapse').forEach((groupEl) => {
          const collapsibleGroupEl = groupEl;
          collapsibleGroupEl.classList.add('show');
          collapsibleGroupEl.style.height = 'auto';
        });
        container.querySelectorAll<HTMLElement>('.builder-group-button').forEach((buttonEl) => {
          buttonEl.setAttribute('aria-expanded', 'true');
        });
        ensureSettingsDocked();

        // Per-field resize, drag-safe: sync() applies stored sizes and observes new fields, but only
        // when called (after builds and on each post-drop 'change') and never while a drag is active —
        // unlike the old live MutationObserver, which mutated the DOM mid-drag and broke drag-and-drop.
        fieldResize = attachFieldResize(
          container,
          () => initialSchemaRef.current,
          (nextSchema) => {
            initialSchemaRef.current = nextSchema;
            onChangeRef.current(nextSchema);
          },
        );
        window.setTimeout(() => {
          fieldResize?.sync();
          ensureSettingsDocked();
          if (builderRef.current && !isBuilderDragging()) {
            applyStoredRichText(builderRef.current, initialSchemaRef.current);
            applyBindingBadges(builderRef.current, initialSchemaRef.current, (item) => bindingLabelByKeyRef.current[item] ?? item);
          }
        });

        instance.on?.('change', () => {
          // Ignore 'change' events fired during teardown: destroy() can emit a final change carrying an
          // emptied schema, which would clobber the caller's saved formSchema to blank (resetting the
          // canvas and disabling Save when the dialog steps away from the builder and back).
          if (!active) return;
          const nextSchema: CustomReportFormSchemaDto = {
            ...mergeStoredComponentStyles(
              normaliseCustomReportFormSchema(instance.schema ?? instance.form),
              initialSchemaRef.current,
            ),
            logoPosition: logoPositionRef.current,
            ...(logoWidthRef.current ? { logoWidth: logoWidthRef.current } : {}),
          };
          initialSchemaRef.current = nextSchema;
          setLabelColours(collectLabelColours(nextSchema));
          onChangeRef.current(nextSchema);
          window.setTimeout(() => {
            fieldResize?.sync();
            ensureSettingsDocked();
            if (builderRef.current && !isBuilderDragging()) {
              applyStoredRichText(builderRef.current, nextSchema);
              applyBindingBadges(builderRef.current, nextSchema, (item) => bindingLabelByKeyRef.current[item] ?? item);
            }
            // Re-apply the selection highlight after Form.io redraws the canvas.
            if (selectedKeyRef.current && builderRef.current) {
              formioComponentElementFor(builderRef.current, selectedKeyRef.current)
                ?.setAttribute('data-custom-report-selected', 'true');
            }
          });
        });
      })
      .catch(() => {
        if (!active) return;
        setIsLoading(false);
        setHasError(true);
      });

    return () => {
      active = false;
      builderInstanceRef.current = undefined;
      container.removeEventListener('dblclick', handleDoubleClick);
      container.removeEventListener('click', handleClick);
      fieldResize?.detach();
      detachEditDialogFlattening();
      builder?.destroy?.();
      builderRef.current?.replaceChildren();
    };
  }, []);

  const selectedLabelColour = selectedKey ? labelColours[selectedKey] : undefined;
  const canColourLabel = Boolean(selectedKey && selectedType && !LAYOUT_TYPES.has(selectedType));
  const canBindField = Boolean(selectedKey && selectedType && !LAYOUT_TYPES.has(selectedType) && bindingGroups?.length);
  // A datagrid fills a whole repeating table, so it can only bind to a collection source (e.g. Pours);
  // every other field binds to a single value. Offer only the matching kind so an author can't wire a
  // text box to a table (or vice-versa) — which would never resolve.
  const wantsCollectionBinding = selectedType === 'datagrid';

  const selectedTypeLabel = selectedType ? (FIELD_TYPE_LABELS[selectedType] ?? selectedType) : undefined;
  const isLayoutSelected = Boolean(selectedType && LAYOUT_TYPES.has(selectedType));
  const canRenameSelected = Boolean(selectedKey && selectedType && !isLayoutSelected && selectedType !== 'content');

  const settingsInputSx = {
    bgcolor: '#242222',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '6px',
    color: '#edecf2',
    font: 'inherit',
    fontSize: 13,
    px: 1,
    py: 0.5,
    width: '100%',
  };

  return (
    <Box sx={{ ...builderShellSx, ...labelColourSx('.formarea', labelColours) }}>
      <GlobalStyles styles={formioDialogGlobalStyles} />
      {hasError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Unable to load the form builder.
        </Alert>
      )}
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
          <CircularProgress size={18} color="secondary" />
          <Typography variant="body2" sx={{ color: '#4b5563' }}>{i18n._('Loading form builder...')}</Typography>
        </Box>
      )}
      {/* One-page layout: field settings (far left, via order:-1) | palette (inside the Form.io markup)
          | canvas — with the dialog's live preview docked to the right of this whole builder. Components
          and field editing sit on one side, the preview on the other. */}
      <Box sx={{ alignItems: 'flex-start', display: isLoading || hasError ? 'none' : 'flex', gap: '16px' }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0, position: 'relative' }}>
          {/* Letterhead band: overlaid on the canvas paper's reserved top padding (formarea paddingTop),
              so the palette runs the full panel height with no dead space above it. The left offset
              tracks the 260px palette + the builder's 16px gap. */}
          <Box sx={{ left: '292px', position: 'absolute', right: '28px', top: '10px', zIndex: 5 }}>
            <CustomReportLogoBand
              logoDataUri={logoDataUri}
              position={logoPosition}
              width={logoWidth}
              onPositionChange={updateLogoPosition}
              onWidthChange={updateLogoWidth}
              action={<CustomReportLogoUploadButton hasLogo={Boolean(logoDataUri)} isUploading={isUploadingLogo} onUpload={uploadLogo} />}
              // Only the builder outlines the band — it marks the area the logo can be dragged within.
              sx={{
                border: `1px dashed ${CANVAS_EDGE}`,
                borderRadius: 1,
                minWidth: 0,
              }}
            />
          </Box>
          <Box ref={builderRef} sx={{ minHeight: 360 }} />
        </Box>
        {/* Rendered here but re-parented into Form.io's palette sidebar by ensureSettingsDocked(), so
            the components list and field settings read as one continuous panel. */}
        <Box
          ref={settingsPanelRef}
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            mt: 1.25,
            pt: 1.25,
            width: '100%',
          }}
        >
          <Box sx={{ bgcolor: '#2f2d2d', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 1, p: 1 }}>
            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: selectedKey ? 0.75 : 0.5 }}>
              <Typography sx={{ color: '#e5e7eb', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                Field settings
              </Typography>
              {selectedTypeLabel && (
                <Box
                  sx={{
                    bgcolor: 'rgba(109,92,255,0.20)',
                    border: '1px solid rgba(109,92,255,0.45)',
                    borderRadius: '4px',
                    color: '#b9b0ff',
                    fontSize: 10.5,
                    fontWeight: 700,
                    px: 0.75,
                    py: 0.1,
                  }}
                >
                  {selectedTypeLabel}
                </Box>
              )}
            </Box>
            {!selectedKey && (
              <Typography sx={{ color: '#9a9aa0', fontSize: 12 }}>
                Click a field on the canvas to edit it here.
              </Typography>
            )}
            {selectedKey && isLayoutSelected && (
              <Typography sx={{ color: '#9a9aa0', fontSize: 12 }}>
                {selectedType === 'content'
                  ? 'Double-click the text block on the canvas to type and colour its text.'
                  : 'Double-click this component on the canvas to open its layout settings.'}
              </Typography>
            )}
            {canRenameSelected && (
              <>
                <Typography sx={{ color: '#9a9aa0', fontSize: 11.5, fontWeight: 700, mb: 0.25 }}>{i18n._('Label')}</Typography>
                <Box
                  component="input"
                  value={selectedLabelText}
                  placeholder={i18n._('Field label')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedLabelText(event.target.value)}
                  onBlur={() => {
                    const value = selectedLabelText.trim();
                    if (selectedKey && value) commitComponentLabel(selectedKey, value);
                  }}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                  }}
                  sx={{ ...settingsInputSx, mb: 1 }}
                />
                <Box component="label" sx={{ alignItems: 'center', color: '#e5e7eb', cursor: 'pointer', display: 'flex', fontSize: 13, gap: 0.75 }}>
                  <Box
                    component="input"
                    type="checkbox"
                    checked={selectedRequired}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => applyRequired(event.target.checked)}
                    sx={{ accentColor: '#4529de', height: 15, m: 0, width: 15 }}
                  />
                  Required
                </Box>
              </>
            )}
          </Box>
          {canColourLabel && (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: '#2f2d2d',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              p: 1,
            }}
          >
            <Typography sx={{ color: '#e5e7eb', flex: '1 1 100%', fontSize: 11.5, fontWeight: 700 }}>
              Label colour
            </Typography>
            {LABEL_COLOURS.map((colour) => (
              <Box
                key={colour}
                component="button"
                type="button"
                title={colour}
                onClick={() => applyLabelColour(colour)}
                sx={{
                  bgcolor: colour,
                  border: selectedLabelColour === colour ? '2px solid #6d5cff' : '1px solid rgba(255,255,255,0.22)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  height: 18,
                  p: 0,
                  width: 18,
                }}
              />
            ))}
            <Box
              component="input"
              type="color"
              value={selectedLabelColour ?? '#111827'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => applyLabelColour(event.target.value)}
              sx={{ bgcolor: 'transparent', border: '1px solid rgba(255,255,255,0.22)', borderRadius: '4px', cursor: 'pointer', height: 24, ml: 0.5, p: 0, width: 26 }}
            />
            <Button size="small" color="secondary" onClick={() => applyLabelColour(undefined)} sx={{ minWidth: 0, px: 1 }}>{i18n._('Reset')}</Button>
          </Box>
          )}
          {canBindField && Boolean(bindingGroups?.length) && (
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: '#2f2d2d',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                p: 1,
              }}
            >
              <Typography sx={{ color: '#e5e7eb', flex: '1 1 100%', fontSize: 11.5, fontWeight: 700 }}>
                Bound data
              </Typography>
              <Box
                component="select"
                value={selectedBinding ?? ''}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => applyBinding(event.target.value || undefined)}
                sx={{
                  bgcolor: '#242222',
                  border: '1px solid rgba(255,255,255,0.16)',
                  borderRadius: '6px',
                  color: '#edecf2',
                  font: 'inherit',
                  fontSize: 13,
                  minWidth: 0,
                  px: 1,
                  py: 0.5,
                  width: '100%',
                }}
              >
                <option value="">{i18n._('Static (no data)')}</option>
                {(bindingGroups ?? []).map((group) => {
                  const items = (group.items ?? []).filter(
                    // `isCollection` is added to the binding DTO by NSwag regen; cast so the build doesn't
                    // depend on the regen having run yet (it resolves to undefined → treated as a scalar).
                    (item) => Boolean((item as { isCollection?: boolean }).isCollection) === wantsCollectionBinding,
                  );
                  if (items.length === 0) return null;
                  return (
                    <optgroup key={group.name} label={group.name}>
                      {items.map((item) => (
                        <option key={item.key} value={item.key}>{item.label}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </Box>
              <Typography sx={{ color: '#9a9aa0', fontSize: 11, flex: '1 1 100%' }}>
                {wantsCollectionBinding
                  ? 'A bound table fills every row automatically when the report is generated for a stope or pour.'
                  : 'Bound fields fill automatically when the report is generated for a stope or pour.'}
              </Typography>
            </Box>
          )}
          {selectedType && CHOICE_TYPES.has(selectedType) && (
            <Box sx={{ bgcolor: '#2f2d2d', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 1, p: 1 }}>
              <Typography sx={{ color: '#e5e7eb', fontSize: 12, fontWeight: 700, mb: 0.75 }}>{i18n._('Options for the selected field')}</Typography>
              <Stack spacing={0.5} sx={{ mb: 0.75 }}>
                {optionRows.length === 0 && (
                  <Typography sx={{ color: '#9a9aa0', fontSize: 12 }}>No options yet — add the choices crews can pick.</Typography>
                )}
                {optionRows.map((option, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={index} sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                    <Box
                      component="input"
                      value={option.label}
                      placeholder={`Option ${index + 1}`}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateOptionLabel(index, event.target.value)}
                      onBlur={() => commitOptions(optionRows)}
                      sx={{
                        bgcolor: '#242222',
                        border: '1px solid rgba(255,255,255,0.16)',
                        borderRadius: '6px',
                        color: '#edecf2',
                        flex: 1,
                        font: 'inherit',
                        fontSize: 13,
                        minWidth: 0,
                        px: 1,
                        py: 0.5,
                      }}
                    />
                    <Box
                      component="button"
                      type="button"
                      title={i18n._('Remove option')}
                      onClick={() => removeOption(index)}
                      sx={{ bgcolor: 'transparent', border: 0, color: '#9a9aa0', cursor: 'pointer', fontSize: 16, lineHeight: 1, px: 0.5, '&:hover': { color: '#e05656' } }}
                    >
                      ×
                    </Box>
                  </Box>
                ))}
              </Stack>
              <Button size="small" color="secondary" onClick={addOption} startIcon={<span style={{ fontWeight: 700 }}>+</span>}>
                Add option
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const CustomReportFormioRenderer = ({
  schema,
  values,
  onChange,
  defaultLogoDataUri,
  onReadyChange,
  printMode = false,
}: CustomReportFormioRendererProps) => {
  const formRef = React.useRef<HTMLDivElement | null>(null);
  const onChangeRef = React.useRef(onChange);
  const onReadyChangeRef = React.useRef(onReadyChange);
  // The form is created once (keyed on schema); these let us push later `values` changes into the live
  // form — resolved binding data and approver rows arrive after mount, so applying them only at creation
  // time would leave the form (and its captured PDF) blank.
  const formInstanceRef = React.useRef<FormioInstance | undefined>(undefined);
  const appliedValuesRef = React.useRef<string>('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const { logoDataUri } = useReportLogo(defaultLogoDataUri);

  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
  }, [onReadyChange]);

  React.useEffect(() => {
    if (!formRef.current || !schema) return undefined;

    let active = true;
    let form: FormioInstance | undefined;

    setIsLoading(true);
    setHasError(false);
    onReadyChangeRef.current?.(false);

    loadFormio()
      .then((Formio) => {
        if (!active || !formRef.current) return undefined;
        return Formio.createForm(formRef.current, normaliseCustomReportFormSchema(schema), {
          ...getFormioI18nOptions(),
          noAlerts: true,
          noDefaultSubmitButton: true,
        });
      })
      .then((instance?: FormioInstance) => {
        if (!instance) return;
        if (!active) {
          instance.destroy?.(true);
          return;
        }

        form = instance;
        formInstanceRef.current = instance;
        form.submission = { data: values };
        appliedValuesRef.current = JSON.stringify(values ?? {});

        const reportData = (event?: { data?: Record<string, unknown> }) => {
          const data = event?.data ?? instance.submission?.data ?? {};
          // Mark form-originated data as already applied, so the values-sync effect below doesn't reset
          // the submission on every keystroke (which would disrupt typing in the fill dialog).
          appliedValuesRef.current = JSON.stringify(data ?? {});
          onChangeRef.current(data);
        };

        if (formRef.current) {
          applyStoredComponentSizes(formRef.current, normaliseCustomReportFormSchema(schema));
          // Show the author's coloured field text (read-only) in the fill view and the PDF.
          applyStoredRichText(formRef.current, normaliseCustomReportFormSchema(schema));
        }
        setIsLoading(false);
        onReadyChangeRef.current?.(true);
        instance.on?.('change', (event) => {
          reportData(event);
          if (formRef.current) applyStoredRichText(formRef.current, normaliseCustomReportFormSchema(schema));
        });
        instance.on?.('submit', (event) => reportData(event));
      })
      .catch(() => {
        if (!active) return;
        setIsLoading(false);
        setHasError(true);
        onReadyChangeRef.current?.(false);
      });

    return () => {
      active = false;
      formInstanceRef.current = undefined;
      onReadyChangeRef.current?.(false);
      form?.destroy?.(true);
    };
  }, [schema]);

  // Push later `values` changes into the already-created form (resolved data / approver rows arrive after
  // mount). Guarded by a JSON compare so the form's own change event doesn't loop back into a re-apply.
  React.useEffect(() => {
    const instance = formInstanceRef.current;
    if (!instance) return;
    const next = JSON.stringify(values ?? {});
    if (next === appliedValuesRef.current) return;
    appliedValuesRef.current = next;
    try {
      instance.submission = { data: values };
    } catch {
      // A transient Form.io state won't lose data — the schema-keyed effect re-seeds on remount.
    }
  }, [values]);

  return (
    <Box sx={{ ...(printMode ? printRendererShellSx : rendererShellSx), ...labelColourSx('.formio-form', collectLabelColours(normaliseCustomReportFormSchema(schema))) }}>
      {logoDataUri && (
        <CustomReportLogoBand
          logoDataUri={logoDataUri}
          position={logoPositionFromSchema(schema)}
          width={logoWidthFromSchema(schema)}
          sx={{ mb: 1.5 }}
        />
      )}
      {hasError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Unable to load the form.
        </Alert>
      )}
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
          <CircularProgress size={18} color="secondary" />
          <Typography variant="body2" color="text.secondary">{i18n._('Loading form...')}</Typography>
        </Box>
      )}
      <Box ref={formRef} sx={{ minHeight: 240, display: isLoading || hasError ? 'none' : 'block' }} />
    </Box>
  );
};
